/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - PRE-CONFIGURED PRINT TEMPLATE PRESETS
 * =========================================================================================
 *
 * MODULE OVERVIEW:
 * This module exports standard, modern, and advanced print presets specifically engineered
 * for Jewellery ERP operations (Sales Billing, Purchase/Old Gold Buyback, Tags, and Rolls).
 *
 * PRESET CATALOGUE BY PAPER SIZE & BILLING TYPE:
 *
 * 1. A4 STANDARD SHEET (210 × 297 mm):
 *    - a4Sales: Flagship Luxury Jewellery Tax Invoice (10-column table, HUID badge, exchange credit, QR).
 *    - a4Purchase: Official Jewellery Purchase & Old Gold Voucher (Assaying, melting loss, statutory declaration).
 *
 * 2. A5 SHEET / PASSBOOK (148 × 210 mm):
 *    - a5Sales: Executive Jewellery Sales Invoice (Compact high-density showroom bill).
 *    - a5Purchase: Old Gold Purchase & Exchange Slip (Counter gold buyback voucher).
 *
 * 3. 80MM POS THERMAL ROLL (80 × 160 mm):
 *    - thermal80mmSale: Modern 80mm POS Jewellery Sales Bill (Item table, GST 3%, UPI QR, Barcode).
 *    - thermal80mmPurchase: 80mm Old Gold Buying Slip (Touch purity, net pure gold, payout).
 *
 * 4. 58MM COMPACT THERMAL ROLL (58 × 130 mm):
 *    - thermal58mmSale: Compact Jewellery Sales Receipt (Compact counter bill with UPI QR).
 *    - thermal58mmPurchase: Compact Old Gold Buyback Slip (Quick cash purchase voucher).
 *
 * 5. JEWELLERY TAGS & LABELS:
 *    - ratTailTag: Rat-Tail Jewellery Barcode Tag (100 × 12 mm dumbbell label for rings/chains).
 *    - rfidTag: RFID Smart Jewellery Tag (40 × 25 mm adhesive tray/box label with QR & Barcode).
 *
 * BACKWARD COMPATIBILITY:
 * Standard legacy keys (a4Invoice, a5Receipt, thermal80mm, thermal57mm, thermal57mmSale, thermal57mmPurchase) are preserved as aliases.
 * =========================================================================================
 */

import { DEFAULT_PRESET_LOGO } from "./defaultPresetLogo.js";
export { DEFAULT_PRESET_LOGO };

export const SAMPLE_TEMPLATES = {
  // =======================================================================================
  // 1. A4 LUXURY JEWELLERY TAX INVOICE (SALES BILLING)
  // =======================================================================================
  a4Sales: {
    id: "a4Sales",
    name: "A4 Luxury Jewellery Tax Invoice (Sales)",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "SALE INVOICE",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [8, 8, 8, 8]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 194,
            height: 281,
            borderWidth: 0.5,
            borderColor: "#0f2d59"
          },

          // Showroom Brand Header
          {
            name: "showroomTitle",
            type: "text",
            position: { x: 12, y: 11 },
            width: 132,
            height: 7,
            content: "{{CompanyName}}",
            fontSize: 16,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "showroomTagline",
            type: "text",
            position: { x: 12, y: 18.5 },
            width: 132,
            height: 4,
            content: "CERTIFIED 916 HALLMARKED GOLD • DIAMOND • BULLION MERCHANTS",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#92400e"
          },
          {
            name: "showroomDetails",
            type: "text",
            position: { x: 12, y: 23 },
            width: 132,
            height: 11,
            content: "{{BranchAddress}}\nPh: {{Phone}} | Email: {{ShowroomEmail}} | Web: {{ShowroomWebsite}}\nGSTIN: {{GSTIN}} | Place of Supply: {{StateCode}} | PAN: {{PAN}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#334155"
          },

          // BIS Hallmark & HUID Laser Badge (Top Right)
          {
            name: "huidBox",
            type: "rectangle",
            position: { x: 148, y: 11 },
            width: 50,
            height: 23,
            borderWidth: 0.5,
            borderColor: "#b45309"
          },
          {
            name: "huidTitle",
            type: "text",
            position: { x: 149, y: 12.5 },
            width: 48,
            height: 4,
            content: "★ BIS 916 HALLMARKED ★",
            fontSize: 7,
            alignment: "center",
            fontColor: "#b45309"
          },
          {
            name: "huidMark",
            type: "text",
            position: { x: 149, y: 17 },
            width: 48,
            height: 5,
            content: "HUID: {{HUID}}",
            fontSize: 9.5,
            alignment: "center",
            fontColor: "#0f2d59"
          },
          {
            name: "huidSub",
            type: "text",
            position: { x: 149, y: 22.5 },
            width: 48,
            height: 8,
            content: "Govt Laser Authenticated\nUnique Identification Mark\n100% Purity Certified",
            fontSize: 5,
            alignment: "center",
            fontColor: "#64748b"
          },

          // Document Banner
          {
            name: "bannerBg",
            type: "rectangle",
            position: { x: 8, y: 35.5 },
            width: 194,
            height: 7,
            borderWidth: 0,
            color: "#0f2d59"
          },
          {
            name: "bannerTitle",
            type: "text",
            position: { x: 10, y: 36.5 },
            width: 190,
            height: 5,
            content: "TAX INVOICE — RETAIL JEWELLERY SALES (RULE 46 - GST)",
            fontSize: 9,
            alignment: "center",
            fontColor: "#ffffff"
          },

          // Left Box: Billed To / Customer Details
          {
            name: "custBox",
            type: "rectangle",
            position: { x: 12, y: 44 },
            width: 92,
            height: 23,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "custBoxTitle",
            type: "text",
            position: { x: 14, y: 45 },
            width: 88,
            height: 3.5,
            content: "BILLED TO / CUSTOMER DETAILS",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "custDetails",
            type: "text",
            position: { x: 14, y: 49 },
            width: 88,
            height: 17,
            content: "Name: {{CustomerName}}\nAddress: {{Address}}\nMobile: {{MobileNo}} | Client Code: {{CustomerCode}}\nCustomer PAN: {{PAN}} | Aadhaar: {{AadharNo}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#1e293b"
          },

          // Right Box: Invoice & Supply Meta
          {
            name: "invBox",
            type: "rectangle",
            position: { x: 108, y: 44 },
            width: 90,
            height: 23,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "invBoxTitle",
            type: "text",
            position: { x: 110, y: 45 },
            width: 86,
            height: 3.5,
            content: "INVOICE & DESPATCH DETAILS",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "invDetails",
            type: "text",
            position: { x: 110, y: 49 },
            width: 86,
            height: 17,
            content: "Invoice No: {{InvoiceNo}}\nDate: {{InvoiceDate}} | Time: {{Time}}\nDaily Gold Rate (22K): ₹ {{GoldRate}} / gm\nPlace of Supply: {{StateCode}} | Staff: {{CashierName}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#1e293b"
          },

          // 10-Column Itemized Jewellery Table
          {
            name: "itemsTable",
            type: "table",
            position: { x: 12, y: 69 },
            width: 186,
            height: 68,
            showHead: true,
            head: [
              "Sl",
              "Item Code",
              "Description",
              "Purity",
              "Gross Wt",
              "Stone Wt",
              "Net Wt",
              "VA %",
              "Rate/g (₹)",
              "Total (₹)"
            ],
            headWidthPercentages: [5, 10, 23, 8, 8, 8, 8, 7, 10, 13],
            content: JSON.stringify([
              ["1", "RNG-22K-104", "22K Hallmarked Antique Ring", "916 (22K)", "5.850", "0.350", "5.500", "12.0%", "6,950", "42,812.00"],
              ["2", "CHN-22K-208", "22K Royal Rope Chain", "916 (22K)", "16.200", "0.000", "16.200", "10.0%", "6,950", "1,23,849.00"],
              ["3", "BNG-22K-055", "22K Traditional Kangan", "916 (22K)", "24.500", "1.200", "23.300", "11.5%", "6,950", "1,80,568.00"]
            ]),
            tableStyles: {
              borderWidth: 0.3,
              borderColor: "#334155"
            },
            headStyles: {
              fontSize: 6.5,
              fontColor: "#ffffff",
              backgroundColor: "#0f2d59",
              borderColor: "#0f2d59",
              borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
              padding: { top: 1.8, right: 1, bottom: 1.8, left: 1 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            bodyStyles: {
              fontSize: 6,
              fontColor: "#0f172a",
              borderColor: "#cbd5e1",
              borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
              padding: { top: 1.5, right: 1, bottom: 1.5, left: 1 },
              alignment: "center",
              verticalAlignment: "middle",
              alternateBackgroundColor: "#f8fafc"
            },
            columnStyles: {
              alignment: {
                "0": "center",
                "1": "center",
                "2": "left",
                "3": "center",
                "4": "right",
                "5": "right",
                "6": "right",
                "7": "right",
                "8": "right",
                "9": "right"
              }
            }
          },

          // Left Box: Old Gold Exchange Deduction Box
          {
            name: "exchangeBox",
            type: "rectangle",
            position: { x: 12, y: 140 },
            width: 92,
            height: 32,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "exchangeTitle",
            type: "text",
            position: { x: 14, y: 141.5 },
            width: 88,
            height: 3.5,
            content: "OLD GOLD EXCHANGE CREDIT / DEDUCTION",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#047857"
          },
          {
            name: "exchangeDetails",
            type: "text",
            position: { x: 14, y: 146 },
            width: 88,
            height: 24,
            content: "Old Gold Received: 22K Scrap Ornaments\nGross Wt: 6.200g | Less Melting/Dross: 0.400g\nNet Gold Wt: 5.800g @ Buying Rate ₹ 6,650 / g\nTotal Exchange Credit: ₹ 38,570.00\n(Deducted directly from current invoice payable)",
            fontSize: 6,
            alignment: "left",
            fontColor: "#334155"
          },

          // Left Box 2: Bank Settlement & UPI QR Code
          {
            name: "bankBox",
            type: "rectangle",
            position: { x: 12, y: 174 },
            width: 92,
            height: 36,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "bankTitle",
            type: "text",
            position: { x: 14, y: 175.5 },
            width: 55,
            height: 3.5,
            content: "BANK SETTLEMENT & UPI",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "bankDetails",
            type: "text",
            position: { x: 14, y: 180 },
            width: 55,
            height: 28,
            content: "Bank: {{BankName}}\nA/C No: {{BankAccountNo}}\nIFSC: {{BankIFSC}}\nBranch: {{BankBranch}}\nUPI VPA: {{UPI_ID}}",
            fontSize: 5.5,
            alignment: "left",
            fontColor: "#334155"
          },
          {
            name: "upiQr",
            type: "qrcode",
            position: { x: 74, y: 177 },
            width: 26,
            height: 26,
            content: "upi://pay?pa=shergold@upi&pn=SherGoldJewellers&cu=INR"
          },
          {
            name: "upiScanHint",
            type: "text",
            position: { x: 72, y: 204 },
            width: 30,
            height: 3,
            content: "Scan with GPay / PhonePe",
            fontSize: 4.5,
            alignment: "center",
            fontColor: "#64748b"
          },

          // Right Box: Financial Summary & Tax Computation
          {
            name: "totalBox",
            type: "rectangle",
            position: { x: 108, y: 140 },
            width: 90,
            height: 70,
            borderWidth: 0.3,
            borderColor: "#0f2d59"
          },
          {
            name: "totalsLabels",
            type: "text",
            position: { x: 111, y: 142 },
            width: 50,
            height: 48,
            content: "Items Gross Total:\nLess Old Gold Exchange:\nTaxable Value:\nCGST @ 1.5%:\nSGST @ 1.5%:\nRound Off:",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#334155"
          },
          {
            name: "totalsValues",
            type: "text",
            position: { x: 161, y: 142 },
            width: 34,
            height: 48,
            content: "₹ 3,47,229.00\n- ₹ 38,570.00\n₹ 3,08,659.00\n₹ 4,629.89\n₹ 4,629.89\n- ₹ 0.78",
            fontSize: 6.5,
            alignment: "right",
            fontColor: "#0f172a"
          },
          {
            name: "totalsDivider",
            type: "line",
            position: { x: 110, y: 191 },
            width: 86,
            height: 0.5,
            color: "#0f2d59"
          },
          {
            name: "grandTotalLabel",
            type: "text",
            position: { x: 111, y: 194 },
            width: 44,
            height: 12,
            content: "NET GRAND TOTAL:\n(Inclusive of All Taxes)",
            fontSize: 7,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "grandTotalValue",
            type: "text",
            position: { x: 155, y: 194 },
            width: 40,
            height: 12,
            content: "₹ 3,17,918.00",
            fontSize: 10.5,
            alignment: "right",
            fontColor: "#0f2d59"
          },

          // Amount in Words & Barcode Strip
          {
            name: "wordsBox",
            type: "rectangle",
            position: { x: 12, y: 213 },
            width: 130,
            height: 14,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "wordsLabel",
            type: "text",
            position: { x: 14, y: 214.5 },
            width: 126,
            height: 3.5,
            content: "AMOUNT IN WORDS (INR):",
            fontSize: 5.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "wordsValue",
            type: "text",
            position: { x: 14, y: 218.5 },
            width: 126,
            height: 7.5,
            content: "{{AmountInWords}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f172a"
          },
          {
            name: "invBarcode",
            type: "code128",
            position: { x: 146, y: 213 },
            width: 52,
            height: 10,
            content: "INV-2026-0842"
          },
          {
            name: "invBarcodeLabel",
            type: "text",
            position: { x: 146, y: 224 },
            width: 52,
            height: 3,
            content: "INV-2026-0842",
            fontSize: 5,
            alignment: "center",
            fontColor: "#0f172a"
          },

          // Terms, Legal & Signatures
          {
            name: "dividerBottom",
            type: "line",
            position: { x: 8, y: 230 },
            width: 194,
            height: 0.5,
            color: "#0f2d59"
          },
          {
            name: "termsTitle",
            type: "text",
            position: { x: 12, y: 233 },
            width: 115,
            height: 4,
            content: "TERMS & CONDITIONS OF SALE:",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "termsContent",
            type: "text",
            position: { x: 12, y: 238 },
            width: 115,
            height: 26,
            content: "1. All gold jewellery items sold carry BIS Hallmark certification with verifiable HUID.\n2. In accordance with hallmarking standards, Net Weight = Gross Weight minus stone weight.\n3. Return or exchange accepted within 7 days against original invoice in undamaged state.\n4. Value addition (making charge) covers design artistry and is subject to standard deduction.\n5. Disputes, if any, shall be subject to the exclusive jurisdiction of local courts. E.&O.E.",
            fontSize: 5.5,
            alignment: "left",
            fontColor: "#475569"
          },
          {
            name: "custSignTitle",
            type: "text",
            position: { x: 130, y: 254 },
            width: 32,
            height: 10,
            content: "___________________\nCustomer Signature",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#334155"
          },
          {
            name: "shopSignTitle",
            type: "text",
            position: { x: 164, y: 242 },
            width: 34,
            height: 22,
            content: "For {{CompanyName}}\n\n\n___________________\nAuthorized Signatory",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#0f2d59"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 2. A4 JEWELLERY PURCHASE & OLD GOLD VOUCHER (BUYBACK / REVERSE CHARGE)
  // =======================================================================================
  a4Purchase: {
    id: "a4Purchase",
    name: "A4 Jewellery Purchase & Old Gold Voucher",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "PURCHASE VOUCHER",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [8, 8, 8, 8]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 194,
            height: 281,
            borderWidth: 0.5,
            borderColor: "#064e3b"
          },

          // Header Area
          {
            name: "showroomTitle",
            type: "text",
            position: { x: 12, y: 11 },
            width: 132,
            height: 7,
            content: "{{CompanyName}}",
            fontSize: 16,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "showroomTagline",
            type: "text",
            position: { x: 12, y: 18.5 },
            width: 132,
            height: 4,
            content: "PRECIOUS METALS REFINING • OLD GOLD BUYBACK • BULLION MERCHANTS",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#b45309"
          },
          {
            name: "showroomDetails",
            type: "text",
            position: { x: 12, y: 23 },
            width: 132,
            height: 11,
            content: "{{BranchAddress}}\nPh: {{Phone}} | Email: {{ShowroomEmail}} | Web: {{ShowroomWebsite}}\nGSTIN: {{GSTIN}} | Bullion License: BL-2026-904 | PAN: {{PAN}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#334155"
          },

          // Statutory Compliance Badge (Top Right)
          {
            name: "sec9Box",
            type: "rectangle",
            position: { x: 148, y: 11 },
            width: 50,
            height: 23,
            borderWidth: 0.5,
            borderColor: "#064e3b"
          },
          {
            name: "sec9Title",
            type: "text",
            position: { x: 149, y: 12.5 },
            width: 48,
            height: 4,
            content: "REVERSE CHARGE VOUCHER",
            fontSize: 6.5,
            alignment: "center",
            fontColor: "#064e3b"
          },
          {
            name: "sec9Law",
            type: "text",
            position: { x: 149, y: 17 },
            width: 48,
            height: 5,
            content: "SEC 9(4) GST ACT",
            fontSize: 9,
            alignment: "center",
            fontColor: "#b45309"
          },
          {
            name: "sec9Sub",
            type: "text",
            position: { x: 149, y: 22.5 },
            width: 48,
            height: 8,
            content: "Procurement from Unregistered Person\nOfficial Bullion Melting Record",
            fontSize: 5,
            alignment: "center",
            fontColor: "#64748b"
          },

          // Title Banner
          {
            name: "bannerBg",
            type: "rectangle",
            position: { x: 8, y: 35.5 },
            width: 194,
            height: 7,
            borderWidth: 0,
            color: "#064e3b"
          },
          {
            name: "bannerTitle",
            type: "text",
            position: { x: 10, y: 36.5 },
            width: 190,
            height: 5,
            content: "OFFICIAL JEWELLERY PURCHASE & OLD GOLD VOUCHER",
            fontSize: 9,
            alignment: "center",
            fontColor: "#ffffff"
          },

          // Left Box: Seller / Customer Identification
          {
            name: "sellerBox",
            type: "rectangle",
            position: { x: 12, y: 44 },
            width: 92,
            height: 24,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "sellerBoxTitle",
            type: "text",
            position: { x: 14, y: 45 },
            width: 88,
            height: 3.5,
            content: "SELLER / BENEFICIARY IDENTIFICATION",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "sellerDetails",
            type: "text",
            position: { x: 14, y: 49 },
            width: 88,
            height: 18,
            content: "Seller Name: {{CustomerName}}\nAddress: {{Address}}\nMobile: {{MobileNo}} | Customer ID: {{CustomerCode}}\nPAN Card: {{PAN}} | Aadhaar No: {{AadharNo}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#1e293b"
          },

          // Right Box: Purchase Voucher & Lot Details
          {
            name: "lotBox",
            type: "rectangle",
            position: { x: 108, y: 44 },
            width: 90,
            height: 24,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "lotBoxTitle",
            type: "text",
            position: { x: 110, y: 45 },
            width: 86,
            height: 3.5,
            content: "PURCHASE & ASSAY LOT METADATA",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "lotDetails",
            type: "text",
            position: { x: 110, y: 49 },
            width: 86,
            height: 18,
            content: "Voucher No: {{PurchaseVoucherNo}}\nDate: {{PurchaseDate}} | Time: {{Time}}\nMelting Batch No: LOT-916-0842\nBullion Officer: {{CashierName}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#1e293b"
          },

          // 9-Column Old Gold Assessment Table
          {
            name: "assessmentTable",
            type: "table",
            position: { x: 12, y: 70 },
            width: 186,
            height: 68,
            showHead: true,
            head: [
              "Sl",
              "Item Description",
              "Purity/Carat",
              "Gross Wt (g)",
              "Stone/Wax (g)",
              "Melt Loss %",
              "Net Pure Wt (g)",
              "Rate/g (₹)",
              "Total Value (₹)"
            ],
            headWidthPercentages: [5, 23, 10, 9, 9, 8, 10, 11, 15],
            content: JSON.stringify([
              ["1", "Old 22K Bangles (Cut/Scrap)", "22K (916)", "28.500", "1.200", "1.5%", "26.890", "6,680.00", "1,79,625.00"],
              ["2", "Old 18K Pendant Set", "18K (750)", "12.400", "2.100", "2.0%", "10.094", "5,465.00", "55,164.00"],
              ["3", "Old Gold Chain & Hooks", "22K (916)", "8.300", "0.200", "1.0%", "8.019", "6,680.00", "53,567.00"]
            ]),
            tableStyles: {
              borderWidth: 0.3,
              borderColor: "#334155"
            },
            headStyles: {
              fontSize: 6.5,
              fontColor: "#ffffff",
              backgroundColor: "#064e3b",
              borderColor: "#064e3b",
              borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
              padding: { top: 1.8, right: 1, bottom: 1.8, left: 1 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            bodyStyles: {
              fontSize: 6,
              fontColor: "#0f172a",
              borderColor: "#cbd5e1",
              borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
              padding: { top: 1.5, right: 1, bottom: 1.5, left: 1 },
              alignment: "center",
              verticalAlignment: "middle",
              alternateBackgroundColor: "#f0fdf4"
            },
            columnStyles: {
              alignment: {
                "0": "center",
                "1": "left",
                "2": "center",
                "3": "right",
                "4": "right",
                "5": "right",
                "6": "right",
                "7": "right",
                "8": "right"
              }
            }
          },

          // Left Box: Disbursement / Bank Transfer Settlement
          {
            name: "payoutBox",
            type: "rectangle",
            position: { x: 12, y: 141 },
            width: 92,
            height: 40,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "payoutTitle",
            type: "text",
            position: { x: 14, y: 142.5 },
            width: 88,
            height: 3.5,
            content: "DISBURSEMENT & BANK SETTLEMENT",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "payoutDetails",
            type: "text",
            position: { x: 14, y: 147 },
            width: 88,
            height: 32,
            content: "Payment Mode: {{PaymentMode}}\nBeneficiary Bank: {{BankName}}\nA/C No: {{BankAccountNo}}\nIFSC: {{BankIFSC}} | Branch: {{BankBranch}}\nBank UTR / Transaction Ref: {{BankUTR}}\nSettlement Status: Completed / Fund Transferred",
            fontSize: 6,
            alignment: "left",
            fontColor: "#334155"
          },

          // Right Box: Valuation Summary
          {
            name: "valuationBox",
            type: "rectangle",
            position: { x: 108, y: 141 },
            width: 90,
            height: 40,
            borderWidth: 0.3,
            borderColor: "#064e3b"
          },
          {
            name: "valuationLabels",
            type: "text",
            position: { x: 111, y: 143 },
            width: 50,
            height: 22,
            content: "Total Gross Weight:\nTotal Net Pure Gold:\nGross Assessed Value:\nLess Melting & Assay Fee:",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#334155"
          },
          {
            name: "valuationValues",
            type: "text",
            position: { x: 161, y: 143 },
            width: 34,
            height: 22,
            content: "49.200 gm\n45.003 gm\n₹ 2,88,356.00\n- ₹ 350.00",
            fontSize: 6.5,
            alignment: "right",
            fontColor: "#0f172a"
          },
          {
            name: "valuationDivider",
            type: "line",
            position: { x: 110, y: 166 },
            width: 86,
            height: 0.5,
            color: "#064e3b"
          },
          {
            name: "netPayableLabel",
            type: "text",
            position: { x: 111, y: 169 },
            width: 44,
            height: 10,
            content: "NET PAYABLE TO SELLER:",
            fontSize: 7,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "netPayableValue",
            type: "text",
            position: { x: 155, y: 169 },
            width: 40,
            height: 10,
            content: "₹ 2,88,006.00",
            fontSize: 10.5,
            alignment: "right",
            fontColor: "#064e3b"
          },

          // Statutory Seller Declaration Box (Crucial Legal Protection for Jewellers)
          {
            name: "declarationBox",
            type: "rectangle",
            position: { x: 12, y: 184 },
            width: 186,
            height: 38,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "declarationTitle",
            type: "text",
            position: { x: 14, y: 185.5 },
            width: 182,
            height: 3.5,
            content: "STATUTORY DECLARATION OF OWNERSHIP BY SELLER (BULLION ACQUISITION ACT):",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "declarationBody",
            type: "text",
            position: { x: 14, y: 190 },
            width: 182,
            height: 30,
            content: "I hereby solemnly declare and affirm that I am the sole, lawful, and undisputed owner of the precious metal ornaments/scrap described above, acquired through legitimate personal inheritance/purchase. The articles are completely free from any encumbrance, dispute, pledge, court attachment, or lien. I voluntarily consent to and authorize the destruction, acid testing, and melting of these articles for accurate assay valuation. I confirm having received full and final settlement amount of ₹ 2,88,006.00 (Two Lakh Eighty-Eight Thousand Six Rupees Only) as stated above.",
            fontSize: 5.5,
            alignment: "left",
            fontColor: "#475569"
          },

          // Words & Barcode
          {
            name: "purchaseBarcode",
            type: "code128",
            position: { x: 12, y: 226 },
            width: 50,
            height: 9,
            content: "PUR-2026-0158"
          },
          {
            name: "purchaseBarcodeLabel",
            type: "text",
            position: { x: 12, y: 236 },
            width: 50,
            height: 3,
            content: "PUR-2026-0158",
            fontSize: 5,
            alignment: "center",
            fontColor: "#064e3b"
          },

          // Signatures: Thumb Impression, Seller & Authorized Signatory
          {
            name: "thumbBox",
            type: "rectangle",
            position: { x: 70, y: 226 },
            width: 25,
            height: 26,
            borderWidth: 0.3,
            borderColor: "#cbd5e1"
          },
          {
            name: "thumbLabel",
            type: "text",
            position: { x: 71, y: 248 },
            width: 23,
            height: 3,
            content: "Left Thumb",
            fontSize: 4.5,
            alignment: "center",
            fontColor: "#64748b"
          },
          {
            name: "sellerSign",
            type: "text",
            position: { x: 100, y: 240 },
            width: 44,
            height: 14,
            content: "___________________________\nSignature of Seller",
            fontSize: 6,
            alignment: "center",
            fontColor: "#334155"
          },
          {
            name: "officerSign",
            type: "text",
            position: { x: 148, y: 232 },
            width: 50,
            height: 22,
            content: "For {{CompanyName}}\n\n\n___________________________\nAuthorized Bullion Officer",
            fontSize: 6,
            alignment: "center",
            fontColor: "#064e3b"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 3. A5 EXECUTIVE JEWELLERY SALES INVOICE (HALF-SHEET SALES BILLING)
  // =======================================================================================
  a5Sales: {
    id: "a5Sales",
    name: "A5 Executive Jewellery Sales Invoice",
    paperSpec: "A5 Gold Passbook (148 × 210 mm)",
    voucherType: "SALE INVOICE",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          // Outer Border Frame
          {
            name: "outerFrameA5",
            type: "rectangle",
            position: { x: 6, y: 6 },
            width: 136,
            height: 198,
            borderWidth: 0.5,
            borderColor: "#0f2d59"
          },

          // Header
          {
            name: "companyTitleA5",
            type: "text",
            position: { x: 9, y: 8 },
            width: 90,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "companyMetaA5",
            type: "text",
            position: { x: 9, y: 14 },
            width: 90,
            height: 9,
            content: "{{BranchAddress}}\nPh: {{Phone}} | GSTIN: {{GSTIN}}",
            fontSize: 6,
            alignment: "left",
            fontColor: "#475569"
          },

          // Hallmark Badge
          {
            name: "huidBoxA5",
            type: "rectangle",
            position: { x: 104, y: 8 },
            width: 34,
            height: 15,
            borderWidth: 0.3,
            borderColor: "#b45309"
          },
          {
            name: "huidTitleA5",
            type: "text",
            position: { x: 105, y: 9 },
            width: 32,
            height: 3,
            content: "BIS 916 HALLMARK",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#b45309"
          },
          {
            name: "huidMarkA5",
            type: "text",
            position: { x: 105, y: 12.5 },
            width: 32,
            height: 4,
            content: "HUID: {{HUID}}",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#0f2d59"
          },

          // Banner
          {
            name: "bannerA5",
            type: "rectangle",
            position: { x: 6, y: 24.5 },
            width: 136,
            height: 5,
            borderWidth: 0,
            color: "#0f2d59"
          },
          {
            name: "bannerTitleA5",
            type: "text",
            position: { x: 8, y: 25.2 },
            width: 132,
            height: 4,
            content: "TAX INVOICE — JEWELLERY RETAIL SALE",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#ffffff"
          },

          // Billing Metadata Row
          {
            name: "custInfoA5",
            type: "text",
            position: { x: 9, y: 31 },
            width: 70,
            height: 12,
            content: "Customer: {{CustomerName}}\nMobile: {{MobileNo}} | PAN: {{PAN}}\nAddress: {{Address}}",
            fontSize: 6,
            alignment: "left",
            fontColor: "#1e293b"
          },
          {
            name: "invInfoA5",
            type: "text",
            position: { x: 82, y: 31 },
            width: 56,
            height: 12,
            content: "Invoice No: {{InvoiceNo}}\nDate: {{InvoiceDate}} | Time: {{Time}}\nDaily 22K Rate: ₹ {{GoldRate}} / gm",
            fontSize: 6,
            alignment: "right",
            fontColor: "#1e293b"
          },

          // 7-Column Table
          {
            name: "itemsTableA5",
            type: "table",
            position: { x: 9, y: 45 },
            width: 130,
            height: 48,
            showHead: true,
            head: ["Sl", "Description", "Purity", "Gross Wt", "Net Wt", "Rate/g", "Amount (₹)"],
            headWidthPercentages: [6, 32, 12, 12, 12, 11, 15],
            content: JSON.stringify([
              ["1", "22K Designer Bangle", "916 (22K)", "14.850g", "14.850g", "6,950", "1,03,208.00"],
              ["2", "22K Gold Stud Earrings", "916 (22K)", "3.600g", "3.400g", "6,950", "23,630.00"]
            ]),
            tableStyles: {
              borderWidth: 0.3,
              borderColor: "#334155"
            },
            headStyles: {
              fontSize: 6,
              fontColor: "#ffffff",
              backgroundColor: "#0f2d59",
              borderColor: "#0f2d59",
              borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
              padding: { top: 1.5, right: 0.8, bottom: 1.5, left: 0.8 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            bodyStyles: {
              fontSize: 5.5,
              fontColor: "#0f172a",
              borderColor: "#cbd5e1",
              borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
              padding: { top: 1.3, right: 0.8, bottom: 1.3, left: 0.8 },
              alignment: "center",
              verticalAlignment: "middle",
              alternateBackgroundColor: "#f8fafc"
            },
            columnStyles: {
              alignment: {
                "0": "center",
                "1": "left",
                "2": "center",
                "3": "right",
                "4": "right",
                "5": "right",
                "6": "right"
              }
            }
          },

          // Totals Section
          {
            name: "totalsBoxA5",
            type: "rectangle",
            position: { x: 74, y: 95 },
            width: 65,
            height: 38,
            borderWidth: 0.3,
            borderColor: "#0f2d59"
          },
          {
            name: "totalsLabelsA5",
            type: "text",
            position: { x: 76, y: 97 },
            width: 34,
            height: 22,
            content: "Items Subtotal:\nCGST @ 1.5%:\nSGST @ 1.5%:\nRound Off:",
            fontSize: 6,
            alignment: "left",
            fontColor: "#334155"
          },
          {
            name: "totalsValuesA5",
            type: "text",
            position: { x: 111, y: 97 },
            width: 26,
            height: 22,
            content: "₹ 1,26,838.00\n₹ 1,902.57\n₹ 1,902.57\n- ₹ 0.14",
            fontSize: 6,
            alignment: "right",
            fontColor: "#0f172a"
          },
          {
            name: "divTotalA5",
            type: "line",
            position: { x: 75, y: 120 },
            width: 63,
            height: 0.5,
            color: "#0f2d59"
          },
          {
            name: "netTotalLabelA5",
            type: "text",
            position: { x: 76, y: 122 },
            width: 32,
            height: 8,
            content: "GRAND TOTAL:",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#0f2d59"
          },
          {
            name: "netTotalValA5",
            type: "text",
            position: { x: 106, y: 122 },
            width: 31,
            height: 8,
            content: "₹ 1,30,643.00",
            fontSize: 9,
            alignment: "right",
            fontColor: "#0f2d59"
          },

          // Left Bottom: UPI QR & Barcode
          {
            name: "qrA5",
            type: "qrcode",
            position: { x: 12, y: 98 },
            width: 24,
            height: 24,
            content: "upi://pay?pa=shergold@upi&pn=SherGold&am=130643"
          },
          {
            name: "qrLabelA5",
            type: "text",
            position: { x: 9, y: 124 },
            width: 30,
            height: 4,
            content: "Scan to Pay via UPI",
            fontSize: 5,
            alignment: "center",
            fontColor: "#64748b"
          },
          {
            name: "barcodeA5",
            type: "code128",
            position: { x: 42, y: 104 },
            width: 28,
            height: 10,
            content: "INV-2026-0842"
          },

          // Words
          {
            name: "wordsA5",
            type: "text",
            position: { x: 9, y: 136 },
            width: 130,
            height: 6,
            content: "Amount: {{AmountInWords}}",
            fontSize: 6,
            alignment: "left",
            fontColor: "#0f172a"
          },

          // Terms & Footer Signatures
          {
            name: "divFootA5",
            type: "line",
            position: { x: 6, y: 144 },
            width: 136,
            height: 0.5,
            color: "#0f2d59"
          },
          {
            name: "termsA5",
            type: "text",
            position: { x: 9, y: 147 },
            width: 80,
            height: 16,
            content: "1. Certified BIS Hallmarked 916 Gold.\n2. Exchange accepted within 7 days with bill.\n3. Subject to local jurisdiction. E.&O.E.",
            fontSize: 5,
            alignment: "left",
            fontColor: "#64748b"
          },
          {
            name: "signA5",
            type: "text",
            position: { x: 92, y: 154 },
            width: 46,
            height: 12,
            content: "For {{CompanyName}}\nAuthorized Signatory",
            fontSize: 6,
            alignment: "center",
            fontColor: "#0f2d59"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 4. A5 OLD GOLD PURCHASE & EXCHANGE SLIP (PURCHASE VOUCHER)
  // =======================================================================================
  a5Purchase: {
    id: "a5Purchase",
    name: "A5 Old Gold Purchase & Exchange Slip",
    paperSpec: "A5 Gold Passbook (148 × 210 mm)",
    voucherType: "PURCHASE VOUCHER",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          {
            name: "outerFrameA5",
            type: "rectangle",
            position: { x: 6, y: 6 },
            width: 136,
            height: 198,
            borderWidth: 0.5,
            borderColor: "#064e3b"
          },
          {
            name: "titleA5",
            type: "text",
            position: { x: 9, y: 8 },
            width: 130,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "left",
            fontColor: "#064e3b"
          },
          {
            name: "subTitleA5",
            type: "text",
            position: { x: 9, y: 14 },
            width: 130,
            height: 5,
            content: "{{BranchAddress}} | GSTIN: {{GSTIN}} | Ph: {{Phone}}",
            fontSize: 6,
            alignment: "left",
            fontColor: "#475569"
          },
          {
            name: "bannerA5",
            type: "rectangle",
            position: { x: 6, y: 21 },
            width: 136,
            height: 5,
            borderWidth: 0,
            color: "#064e3b"
          },
          {
            name: "bannerTextA5",
            type: "text",
            position: { x: 8, y: 21.8 },
            width: 132,
            height: 4,
            content: "OLD GOLD PURCHASE & CASH/BANK PAYOUT VOUCHER",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "sellerA5",
            type: "text",
            position: { x: 9, y: 28 },
            width: 70,
            height: 12,
            content: "Seller: {{CustomerName}}\nMobile: {{MobileNo}} | PAN: {{PAN}}\nAadhaar: {{AadharNo}}",
            fontSize: 6,
            alignment: "left",
            fontColor: "#1e293b"
          },
          {
            name: "metaA5",
            type: "text",
            position: { x: 82, y: 28 },
            width: 56,
            height: 12,
            content: "Voucher: {{PurchaseVoucherNo}}\nDate: {{PurchaseDate}} | Time: {{Time}}\nCashier: {{CashierName}}",
            fontSize: 6,
            alignment: "right",
            fontColor: "#1e293b"
          },
          {
            name: "purchaseTableA5",
            type: "table",
            position: { x: 9, y: 42 },
            width: 130,
            height: 44,
            showHead: true,
            head: ["Sl", "Item Description", "Purity", "Gross Wt", "Less Stones", "Net Pure Wt", "Amount (₹)"],
            headWidthPercentages: [6, 32, 12, 12, 12, 11, 15],
            content: JSON.stringify([
              ["1", "Old 22K Scrap Chain", "22K (916)", "12.500g", "0.500g", "12.000g", "80,160.00"],
              ["2", "Old 18K Rings / Studs", "18K (750)", "4.200g", "0.400g", "3.800g", "20,767.00"]
            ]),
            tableStyles: { borderWidth: 0.3, borderColor: "#334155" },
            headStyles: {
              fontSize: 6,
              fontColor: "#ffffff",
              backgroundColor: "#064e3b",
              borderColor: "#064e3b",
              borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
              padding: { top: 1.5, right: 0.8, bottom: 1.5, left: 0.8 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            bodyStyles: {
              fontSize: 5.5,
              fontColor: "#0f172a",
              borderColor: "#cbd5e1",
              borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
              padding: { top: 1.3, right: 0.8, bottom: 1.3, left: 0.8 },
              alignment: "center",
              verticalAlignment: "middle",
              alternateBackgroundColor: "#f0fdf4"
            },
            columnStyles: { alignment: { "0": "center", "1": "left", "2": "center", "3": "right", "4": "right", "5": "right", "6": "right" } }
          },
          {
            name: "summaryA5",
            type: "text",
            position: { x: 74, y: 88 },
            width: 65,
            height: 20,
            content: "Gross Assessed Value: ₹ 1,00,927.00\nLess Assay/Melting Fee: - ₹ 200.00\nNET PAYABLE TO SELLER: ₹ 1,00,727.00",
            fontSize: 6.5,
            alignment: "right",
            fontColor: "#064e3b"
          },
          {
            name: "declA5",
            type: "text",
            position: { x: 9, y: 110 },
            width: 130,
            height: 18,
            content: "SELLER DECLARATION: I solemnly declare that I am the sole and lawful owner of the gold ornaments listed above. I confirm receiving the settlement amount of ₹ 1,00,727.00 in full.",
            fontSize: 5.5,
            alignment: "left",
            fontColor: "#475569"
          },
          {
            name: "signSellerA5",
            type: "text",
            position: { x: 12, y: 132 },
            width: 50,
            height: 10,
            content: "_____________________\nSeller Signature",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#334155"
          },
          {
            name: "signStoreA5",
            type: "text",
            position: { x: 86, y: 132 },
            width: 50,
            height: 10,
            content: "_____________________\nAuthorized Bullion Officer",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#064e3b"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 5. 80MM POS JEWELLERY SALES BILL (THERMAL ROLL)
  // =======================================================================================
  thermal80mmSale: {
    id: "thermal80mmSale",
    name: "80mm Modern POS Jewellery Sales Bill",
    paperSpec: "80mm POS Thermal (80 × 160 mm)",
    voucherType: "SALE INVOICE",
    width: 80,
    height: 160,
    template: {
      basePdf: {
        width: 80,
        height: 160,
        padding: [3, 3, 3, 3]
      },
      schemas: [
        [
          {
            name: "shopName",
            type: "text",
            position: { x: 3, y: 4 },
            width: 74,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 11,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "shopAddress",
            type: "text",
            position: { x: 3, y: 10 },
            width: 74,
            height: 7,
            content: "{{BranchAddress}}\nPh: {{Phone}} | GSTIN: {{GSTIN}}",
            fontSize: 6.5,
            alignment: "center",
            fontColor: "#333333"
          },
          {
            name: "docTitle",
            type: "text",
            position: { x: 3, y: 18 },
            width: 74,
            height: 4.5,
            content: "RETAIL TAX INVOICE",
            fontSize: 8.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "div1",
            type: "line",
            position: { x: 3, y: 23 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "billMetaLeft",
            type: "text",
            position: { x: 3, y: 24.5 },
            width: 38,
            height: 12,
            content: "Inv No: {{InvoiceNo}}\nDate: {{InvoiceDate}}\nCust: {{CustomerName}}",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "billMetaRight",
            type: "text",
            position: { x: 41, y: 24.5 },
            width: 36,
            height: 12,
            content: "Time: {{Time}}\nRate: ₹{{GoldRate}}/g\nMob: {{MobileNo}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "div2",
            type: "line",
            position: { x: 3, y: 37.5 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "posTable",
            type: "table",
            position: { x: 3, y: 39 },
            width: 74,
            height: 32,
            showHead: true,
            head: ["Item", "Purity", "Net Wt", "Rate", "Amount"],
            headWidthPercentages: [30, 15, 18, 17, 20],
            content: JSON.stringify([
              ["22K Ring", "916", "4.200g", "6,950", "31,520"],
              ["Rope Chain", "916", "12.000g", "6,950", "86,736"]
            ]),
            tableStyles: { borderWidth: 0.2, borderColor: "#000000" },
            headStyles: {
              fontSize: 6,
              fontColor: "#ffffff",
              backgroundColor: "#1e293b",
              borderColor: "#1e293b",
              borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
              padding: { top: 1.2, right: 0.6, bottom: 1.2, left: 0.6 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            bodyStyles: {
              fontSize: 5.5,
              fontColor: "#000000",
              borderColor: "#e2e8f0",
              borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
              padding: { top: 1.0, right: 0.6, bottom: 1.0, left: 0.6 },
              alignment: "center",
              verticalAlignment: "middle"
            },
            columnStyles: { alignment: { "0": "left", "1": "center", "2": "right", "3": "right", "4": "right" } }
          },
          {
            name: "div3",
            type: "line",
            position: { x: 3, y: 73 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "totalsText",
            type: "text",
            position: { x: 3, y: 75 },
            width: 74,
            height: 18,
            content: "Subtotal: ₹ 1,18,256.00\nCGST (1.5%): ₹ 1,774.00 | SGST (1.5%): ₹ 1,774.00\nGRAND TOTAL: ₹ 1,21,804.00",
            fontSize: 7.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "qrCode",
            type: "qrcode",
            position: { x: 29, y: 95 },
            width: 22,
            height: 22,
            content: "upi://pay?pa=shergold@upi&pn=SherGold&cu=INR"
          },
          {
            name: "qrText",
            type: "text",
            position: { x: 3, y: 118 },
            width: 74,
            height: 3.5,
            content: "Scan QR with GPay / PhonePe / Paytm",
            fontSize: 6,
            alignment: "center",
            fontColor: "#444444"
          },
          {
            name: "barcode",
            type: "code128",
            position: { x: 12, y: 123 },
            width: 56,
            height: 10,
            content: "INV-2026-0842"
          },
          {
            name: "footerLegal",
            type: "text",
            position: { x: 3, y: 136 },
            width: 74,
            height: 10,
            content: "HUID: {{HUID}} • 100% Hallmarked 916 Gold\nExchange accepted within 7 days. E.&O.E.",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#555555"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 6. 80MM OLD GOLD PURCHASE SLIP (THERMAL ROLL)
  // =======================================================================================
  thermal80mmPurchase: {
    id: "thermal80mmPurchase",
    name: "80mm Old Gold Buying Slip",
    paperSpec: "80mm POS Thermal (80 × 160 mm)",
    voucherType: "PURCHASE VOUCHER",
    width: 80,
    height: 160,
    template: {
      basePdf: {
        width: 80,
        height: 160,
        padding: [3, 3, 3, 3]
      },
      schemas: [
        [
          {
            name: "shopName",
            type: "text",
            position: { x: 3, y: 4 },
            width: 74,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 11,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "docTitle",
            type: "text",
            position: { x: 3, y: 11 },
            width: 74,
            height: 4.5,
            content: "OLD GOLD BUYBACK VOUCHER",
            fontSize: 8.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "div1",
            type: "line",
            position: { x: 3, y: 16.5 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "metaLeft",
            type: "text",
            position: { x: 3, y: 18 },
            width: 38,
            height: 12,
            content: "Vch: {{PurchaseVoucherNo}}\nDate: {{PurchaseDate}}\nSeller: {{CustomerName}}",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "metaRight",
            type: "text",
            position: { x: 41, y: 18 },
            width: 36,
            height: 12,
            content: "Time: {{Time}}\nPAN: {{PAN}}\nMob: {{MobileNo}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "div2",
            type: "line",
            position: { x: 3, y: 31 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "assessmentBody",
            type: "text",
            position: { x: 3, y: 33 },
            width: 74,
            height: 45,
            content: "Gross Weight Received: 18.500 gm\nLess Stones / Enamel: 1.200 gm\nMelting Touch Purity: 91.60% (22K)\nNet Pure Gold Computed: 15.847 gm\nBuying Rate / gm: ₹ 6,680.00\nAssessed Valuation: ₹ 1,05,858.00\nLess Assay/Melting Fee: - ₹ 150.00\n-------------------------------------------------\nNET CASH/BANK PAID: ₹ 1,05,708.00",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "div3",
            type: "line",
            position: { x: 3, y: 80 },
            width: 74,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "declaration",
            type: "text",
            position: { x: 3, y: 82 },
            width: 74,
            height: 18,
            content: "DECLARATION: I certify that I am the sole and lawful owner of the gold sold above. I have received ₹ 1,05,708 in full settlement.",
            fontSize: 6,
            alignment: "left",
            fontColor: "#333333"
          },
          {
            name: "signCustomer",
            type: "text",
            position: { x: 3, y: 104 },
            width: 36,
            height: 10,
            content: "__________________\nSeller Signature",
            fontSize: 6,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "signCashier",
            type: "text",
            position: { x: 41, y: 104 },
            width: 36,
            height: 10,
            content: "__________________\nBullion Officer",
            fontSize: 6,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "barcodePurchase",
            type: "code128",
            position: { x: 12, y: 120 },
            width: 56,
            height: 10,
            content: "PUR-2026-0158"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 7. 58MM COMPACT JEWELLERY SALES BILL (THERMAL ROLL)
  // =======================================================================================
  thermal58mmSale: {
    id: "thermal58mmSale",
    name: "58mm Compact Jewellery Sales Bill",
    paperSpec: "58mm Thermal Roll (58 × 130 mm)",
    voucherType: "SALE INVOICE",
    width: 58,
    height: 130,
    template: {
      basePdf: {
        width: 58,
        height: 130,
        padding: [2, 2, 2, 2]
      },
      schemas: [
        [
          {
            name: "companyTitle",
            type: "text",
            position: { x: 2, y: 3 },
            width: 54,
            height: 5.5,
            content: "{{CompanyName}}",
            fontSize: 9.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyMeta",
            type: "text",
            position: { x: 2, y: 8.5 },
            width: 54,
            height: 6,
            content: "{{BranchAddress}}\nPh: {{Phone}} | GSTIN: {{GSTIN}}",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#333333"
          },
          {
            name: "billTitle",
            type: "text",
            position: { x: 2, y: 15.5 },
            width: 54,
            height: 4,
            content: "TAX INVOICE / CASH BILL",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "div1",
            type: "line",
            position: { x: 2, y: 20 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "metaLeft",
            type: "text",
            position: { x: 2, y: 21 },
            width: 27,
            height: 8,
            content: "Inv: {{InvoiceNo}}\nDate: {{InvoiceDate}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "metaRight",
            type: "text",
            position: { x: 29, y: 21 },
            width: 27,
            height: 8,
            content: "Time: {{Time}}\nRate: ₹{{GoldRate}}",
            fontSize: 6.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "customerRow",
            type: "text",
            position: { x: 2, y: 29.5 },
            width: 54,
            height: 4,
            content: "Customer: {{CustomerName}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "div2",
            type: "line",
            position: { x: 2, y: 34 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "item1",
            type: "text",
            position: { x: 2, y: 35.5 },
            width: 54,
            height: 8,
            content: "1. 22K Ring (916) - 4.200g\n   Rate: ₹6,950/g  Amt: ₹31,520",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "item2",
            type: "text",
            position: { x: 2, y: 44 },
            width: 54,
            height: 8,
            content: "2. 22K Chain (916) - 12.000g\n   Rate: ₹6,950/g  Amt: ₹86,736",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "div3",
            type: "line",
            position: { x: 2, y: 53 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "taxTotals",
            type: "text",
            position: { x: 2, y: 54.5 },
            width: 54,
            height: 12,
            content: "Subtotal: ₹ 1,18,256.00\nGST 3%: ₹ 3,548.00\nGRAND TOTAL: ₹ 1,21,804.00",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "div4",
            type: "line",
            position: { x: 2, y: 68 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "upiQr",
            type: "qrcode",
            position: { x: 20, y: 70 },
            width: 18,
            height: 18,
            content: "upi://pay?pa=shergold@upi&pn=SherGold&am=121804"
          },
          {
            name: "qrSubtext",
            type: "text",
            position: { x: 2, y: 89 },
            width: 54,
            height: 3,
            content: "Scan QR to Pay via UPI",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#555555"
          },
          {
            name: "barcode58",
            type: "code128",
            position: { x: 6, y: 93 },
            width: 46,
            height: 8,
            content: "INV-2026-0842"
          },
          {
            name: "footer58",
            type: "text",
            position: { x: 2, y: 103 },
            width: 54,
            height: 6,
            content: "HUID: {{HUID}} • 100% Hallmarked 916\nThank you for shopping with us!",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#444444"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 8. 58MM OLD GOLD BUYBACK SLIP (THERMAL ROLL)
  // =======================================================================================
  thermal58mmPurchase: {
    id: "thermal58mmPurchase",
    name: "58mm Old Gold Buyback Slip",
    paperSpec: "58mm Thermal Roll (58 × 130 mm)",
    voucherType: "PURCHASE VOUCHER",
    width: 58,
    height: 130,
    template: {
      basePdf: {
        width: 58,
        height: 130,
        padding: [2, 2, 2, 2]
      },
      schemas: [
        [
          {
            name: "companyTitle",
            type: "text",
            position: { x: 2, y: 3 },
            width: 54,
            height: 5.5,
            content: "{{CompanyName}}",
            fontSize: 9.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "billTitle",
            type: "text",
            position: { x: 2, y: 9 },
            width: 54,
            height: 4,
            content: "OLD GOLD BUYING SLIP",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "div1",
            type: "line",
            position: { x: 2, y: 14 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "metaInfo",
            type: "text",
            position: { x: 2, y: 15.5 },
            width: 54,
            height: 12,
            content: "Vch: {{PurchaseVoucherNo}} | Date: {{PurchaseDate}}\nSeller: {{CustomerName}}\nPAN/Mob: {{PAN}} / {{MobileNo}}",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "div2",
            type: "line",
            position: { x: 2, y: 29 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "breakdown",
            type: "text",
            position: { x: 2, y: 31 },
            width: 54,
            height: 30,
            content: "Gross Weight: 12.500 g\nLess Stones/Wax: 0.500 g\nMelting Touch: 91.60%\nNet Pure Gold: 11.000 g\nBuying Rate: ₹ 6,680 / g\nLess Melting Fee: ₹ 100\n-----------------------------------\nNET PAYOUT: ₹ 73,480.00",
            fontSize: 6.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "div3",
            type: "line",
            position: { x: 2, y: 63 },
            width: 54,
            height: 0.5,
            color: "#000000"
          },
          {
            name: "declShort",
            type: "text",
            position: { x: 2, y: 65 },
            width: 54,
            height: 10,
            content: "I confirm that I am the owner of this gold and have received payout in full.",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#444444"
          },
          {
            name: "signatures",
            type: "text",
            position: { x: 2, y: 78 },
            width: 54,
            height: 12,
            content: "Seller Sign               Bullion Officer",
            fontSize: 6,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "barcode58p",
            type: "code128",
            position: { x: 6, y: 92 },
            width: 46,
            height: 8,
            content: "PUR-2026-0158"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 9. JEWELLERY RAT-TAIL BARCODE TAG (100 × 12 mm DUMBBELL)
  // =======================================================================================
  ratTailTag: {
    id: "ratTailTag",
    name: "Jewellery Rat-Tail Barcode Tag (100 × 12 mm)",
    paperSpec: "Rat-Tail Barcode Tag (100 × 12 mm)",
    voucherType: "BARCODE TAG",
    width: 100,
    height: 12,
    template: {
      basePdf: {
        width: 100,
        height: 12,
        padding: [0.5, 0.5, 0.5, 0.5]
      },
      schemas: [
        [
          // Left Dumbbell Wing (0 to 32 mm)
          {
            name: "tagItemCode",
            type: "text",
            position: { x: 1, y: 1 },
            width: 28,
            height: 3,
            content: "SG-RNG-916",
            fontSize: 5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "tagWeights",
            type: "text",
            position: { x: 1, y: 4 },
            width: 28,
            height: 4,
            content: "GW: 4.820g  NW: 4.500g",
            fontSize: 4.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "tagHuid",
            type: "text",
            position: { x: 1, y: 8 },
            width: 28,
            height: 3,
            content: "HUID: K9A4M2  22K916",
            fontSize: 4.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Center Dumbbell Bridge (33 to 68 mm)
          {
            name: "tagBarcode",
            type: "code128",
            position: { x: 34, y: 1.5 },
            width: 32,
            height: 6.5,
            content: "8901234567"
          },
          {
            name: "tagBarcodeNum",
            type: "text",
            position: { x: 34, y: 8.5 },
            width: 32,
            height: 2.5,
            content: "8901234567",
            fontSize: 4,
            alignment: "center",
            fontColor: "#000000"
          },

          // Right Dumbbell Wing (70 to 99 mm)
          {
            name: "tagPrice",
            type: "text",
            position: { x: 70, y: 2 },
            width: 28,
            height: 4,
            content: "₹ 34,500.00",
            fontSize: 6,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "tagShop",
            type: "text",
            position: { x: 70, y: 6.5 },
            width: 28,
            height: 4,
            content: "SHERGOLD",
            fontSize: 5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 10. RFID SMART JEWELLERY TAG (40 × 25 mm ADHESIVE LABEL)
  // =======================================================================================
  rfidTag: {
    id: "rfidTag",
    name: "RFID Smart Jewellery Tag (40 × 25 mm)",
    paperSpec: "RFID Jewelry Tag (40 × 25 mm)",
    voucherType: "RFID TAG",
    width: 40,
    height: 25,
    template: {
      basePdf: {
        width: 40,
        height: 25,
        padding: [1, 1, 1, 1]
      },
      schemas: [
        [
          {
            name: "rfidShop",
            type: "text",
            position: { x: 1, y: 1 },
            width: 38,
            height: 3,
            content: "SHERGOLD JEWELLERS",
            fontSize: 5.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "rfidItem",
            type: "text",
            position: { x: 1, y: 4.5 },
            width: 23,
            height: 3,
            content: "RNG-22K-104",
            fontSize: 5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "rfidPurity",
            type: "text",
            position: { x: 1, y: 7.5 },
            width: 23,
            height: 3,
            content: "22K (916) HALLMARKED",
            fontSize: 4.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "rfidWeights",
            type: "text",
            position: { x: 1, y: 10.5 },
            width: 23,
            height: 3.5,
            content: "GW: 5.850g | NW: 5.500g",
            fontSize: 4.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "rfidHuid",
            type: "text",
            position: { x: 1, y: 14 },
            width: 23,
            height: 3,
            content: "HUID: K9A4M2",
            fontSize: 4.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "rfidPrice",
            type: "text",
            position: { x: 1, y: 17 },
            width: 23,
            height: 3.5,
            content: "₹ 42,812.00",
            fontSize: 6,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "rfidQr",
            type: "qrcode",
            position: { x: 25, y: 4.5 },
            width: 14,
            height: 14,
            content: "https://shergold.com/t/RNG-22K-104"
          },
          {
            name: "rfidBarcode",
            type: "code128",
            position: { x: 1, y: 20 },
            width: 38,
            height: 4,
            content: "RNG-22K-104"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 6. A4 CASH / BANK RECEIPT VOUCHER (SHERSOFT ERP PATTERN)
  // =======================================================================================
  a4Receipt: {
    id: "a4Receipt",
    name: "A4 Cash / Bank Receipt Voucher",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "RECEIPT",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [10, 10, 10, 10]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 10, y: 10 },
            width: 190,
            height: 277,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 10, y: 22 },
            width: 190,
            height: 9,
            content: "RECEIPT",
            fontSize: 18,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 10, y: 32 },
            width: 190,
            height: 7.5,
            content: "{{CompanyName}}",
            fontSize: 14,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 20, y: 48 },
            width: 15,
            height: 6,
            content: "No.",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 38, y: 48 },
            width: 55,
            height: 6,
            content: "{{VoucherNo}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 130, y: 48 },
            width: 60,
            height: 6,
            content: "Date:{{Date}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 38, y: 62 },
            width: 62,
            height: 6,
            content: "Account Number",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 104, y: 62 },
            width: 86,
            height: 6,
            content: "{{AccountNo}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 38, y: 76 },
            width: 62,
            height: 6,
            content: "Received From",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 104, y: 76 },
            width: 86,
            height: 6,
            content: "{{CustomerName}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 38, y: 90 },
            width: 62,
            height: 6,
            content: "The sum of rupees",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 104, y: 90 },
            width: 86,
            height: 6,
            content: "{{AmountInWords}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 38, y: 104 },
            width: 62,
            height: 6,
            content: "By Cash / cheque/DD No",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 104, y: 104 },
            width: 86,
            height: 6,
            content: "{{PaymentMode}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box (Solid Black ₹ left + White Amount right)
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 38, y: 120 },
            width: 16,
            height: 11,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 38, y: 121 },
            width: 16,
            height: 9,
            content: "₹",
            fontSize: 14,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 54, y: 120 },
            width: 66,
            height: 11,
            borderWidth: 0.6,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 56, y: 121.5 },
            width: 60,
            height: 8,
            content: "{{TotalAmount}}",
            fontSize: 12.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 38, y: 133 },
            width: 95,
            height: 5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 38, y: 146 },
            width: 62,
            height: 6,
            content: "Rate/Gm",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 104, y: 146 },
            width: 86,
            height: 6,
            content: "₹ {{Rate}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 38, y: 160 },
            width: 62,
            height: 6,
            content: "Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 104, y: 160 },
            width: 86,
            height: 6,
            content: "{{Weight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 38, y: 174 },
            width: 62,
            height: 6,
            content: "Total Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 104, y: 174 },
            width: 86,
            height: 6,
            content: "{{TotalWeight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 38, y: 185 },
            width: 62,
            height: 6,
            content: "Total Amount",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 104, y: 185 },
            width: 86,
            height: 6,
            content: "₹ {{ClosingBalance}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 38, y: 196 },
            width: 62,
            height: 6,
            content: "Old Balance",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 104, y: 196 },
            width: 86,
            height: 6,
            content: "₹ {{OldBalance}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 125, y: 245 },
            width: 65,
            height: 7,
            content: "Receiver Signature",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 7. A5 CASH / BANK RECEIPT VOUCHER (PORTRAIT PASSBOOK FORMAT)
  // =======================================================================================
  a5Receipt: {
    id: "a5Receipt",
    name: "A5 Cash / Bank Receipt Voucher",
    paperSpec: "A5 Sheet / Passbook (148 × 210 mm)",
    voucherType: "RECEIPT",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 132,
            height: 194,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 8, y: 16 },
            width: 132,
            height: 7,
            content: "RECEIPT",
            fontSize: 15,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 8, y: 23.5 },
            width: 132,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 13, y: 35 },
            width: 12,
            height: 5,
            content: "No.",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 26, y: 35 },
            width: 44,
            height: 5,
            content: "{{VoucherNo}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 80, y: 35 },
            width: 52,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 9.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 26, y: 46 },
            width: 46,
            height: 5,
            content: "Account Number",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 74, y: 46 },
            width: 58,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 26, y: 56 },
            width: 46,
            height: 5,
            content: "Received From",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 74, y: 56 },
            width: 58,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 26, y: 66 },
            width: 46,
            height: 5,
            content: "The sum of rupees",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 74, y: 66 },
            width: 58,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 9,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 26, y: 76 },
            width: 46,
            height: 5,
            content: "By Cash / cheque/DD No",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 74, y: 76 },
            width: 58,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 26, y: 88 },
            width: 13,
            height: 8.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 26, y: 89 },
            width: 13,
            height: 7,
            content: "₹",
            fontSize: 12,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 39, y: 88 },
            width: 54,
            height: 8.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 41, y: 89.5 },
            width: 49,
            height: 6,
            content: "{{TotalAmount}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 26, y: 98.5 },
            width: 75,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 7,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 26, y: 108 },
            width: 46,
            height: 5,
            content: "Rate/Gm",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 74, y: 108 },
            width: 58,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 26, y: 118 },
            width: 46,
            height: 5,
            content: "Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 74, y: 118 },
            width: 58,
            height: 5,
            content: "{{Weight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 26, y: 128 },
            width: 46,
            height: 5,
            content: "Total Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 74, y: 128 },
            width: 58,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 26, y: 136 },
            width: 46,
            height: 5,
            content: "Total Amount",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 74, y: 136 },
            width: 58,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 26, y: 144 },
            width: 46,
            height: 5,
            content: "Old Balance",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 74, y: 144 },
            width: 58,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 72, y: 178 },
            width: 60,
            height: 6,
            content: "Receiver Signature",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 8. A5 LANDSCAPE / HALF-A4 RECEIPT VOUCHER (SHERSOFT ERP EXACT REPLICA)
  // =======================================================================================
  a5LandscapeReceipt: {
    id: "a5LandscapeReceipt",
    name: "A5 Landscape / Half-A4 Receipt Voucher",
    paperSpec: "Half-A4 Sheet (210 × 148 mm)",
    voucherType: "RECEIPT",
    width: 210,
    height: 148,
    template: {
      basePdf: {
        width: 210,
        height: 148,
        padding: [8, 8, 8, 8]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 194,
            height: 132,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 8, y: 14 },
            width: 194,
            height: 6.5,
            content: "RECEIPT",
            fontSize: 15,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 8, y: 21 },
            width: 194,
            height: 5.5,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 16, y: 31 },
            width: 14,
            height: 5,
            content: "No.",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 36, y: 31 },
            width: 50,
            height: 5,
            content: "{{VoucherNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 140, y: 31 },
            width: 56,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 36, y: 40 },
            width: 55,
            height: 5,
            content: "Account Number",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 95, y: 40 },
            width: 65,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 36, y: 48.5 },
            width: 55,
            height: 5,
            content: "Received From",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 95, y: 48.5 },
            width: 65,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 36, y: 57 },
            width: 55,
            height: 5,
            content: "The sum of rupees",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 95, y: 57 },
            width: 65,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 36, y: 65.5 },
            width: 55,
            height: 5,
            content: "By Cash / cheque/DD No",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 95, y: 65.5 },
            width: 65,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 36, y: 75 },
            width: 14,
            height: 8.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 36, y: 76 },
            width: 14,
            height: 7,
            content: "₹",
            fontSize: 12,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 50, y: 75 },
            width: 58,
            height: 8.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 52, y: 76.5 },
            width: 53,
            height: 6,
            content: "{{TotalAmount}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 36, y: 85 },
            width: 75,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 36, y: 93 },
            width: 55,
            height: 5,
            content: "Rate/Gm",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 95, y: 93 },
            width: 65,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 36, y: 101.5 },
            width: 55,
            height: 5,
            content: "Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 95, y: 101.5 },
            width: 65,
            height: 5,
            content: "{{Weight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 36, y: 110 },
            width: 55,
            height: 5,
            content: "Total Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 95, y: 110 },
            width: 65,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 36, y: 117 },
            width: 55,
            height: 5,
            content: "Total Amount",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 95, y: 117 },
            width: 65,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 36, y: 124 },
            width: 55,
            height: 5,
            content: "Old Balance",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 95, y: 124 },
            width: 65,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 135, y: 132 },
            width: 60,
            height: 6,
            content: "Receiver Signature",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 9. A4 CASH / BANK PAYMENT VOUCHER
  // =======================================================================================
  a4Payment: {
    id: "a4Payment",
    name: "A4 Cash / Bank Payment Voucher",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "PAYMENT",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [10, 10, 10, 10]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 10, y: 10 },
            width: 190,
            height: 277,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 10, y: 22 },
            width: 190,
            height: 9,
            content: "PAYMENT",
            fontSize: 18,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 10, y: 32 },
            width: 190,
            height: 7.5,
            content: "{{CompanyName}}",
            fontSize: 14,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 20, y: 48 },
            width: 15,
            height: 6,
            content: "No.",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 38, y: 48 },
            width: 55,
            height: 6,
            content: "{{VoucherNo}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 130, y: 48 },
            width: 60,
            height: 6,
            content: "Date:{{Date}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 38, y: 62 },
            width: 62,
            height: 6,
            content: "Account Number",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 104, y: 62 },
            width: 86,
            height: 6,
            content: "{{AccountNo}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 38, y: 76 },
            width: 62,
            height: 6,
            content: "Paid To",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 104, y: 76 },
            width: 86,
            height: 6,
            content: "{{CustomerName}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 38, y: 90 },
            width: 62,
            height: 6,
            content: "The sum of rupees",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 104, y: 90 },
            width: 86,
            height: 6,
            content: "{{AmountInWords}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 38, y: 104 },
            width: 62,
            height: 6,
            content: "By Cash / cheque/DD No",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 104, y: 104 },
            width: 86,
            height: 6,
            content: "{{PaymentMode}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 38, y: 120 },
            width: 16,
            height: 11,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 38, y: 121 },
            width: 16,
            height: 9,
            content: "₹",
            fontSize: 14,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 54, y: 120 },
            width: 66,
            height: 11,
            borderWidth: 0.6,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 56, y: 121.5 },
            width: 60,
            height: 8,
            content: "{{TotalAmount}}",
            fontSize: 12.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 38, y: 133 },
            width: 95,
            height: 5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 38, y: 146 },
            width: 62,
            height: 6,
            content: "Rate/Gm",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 104, y: 146 },
            width: 86,
            height: 6,
            content: "₹ {{Rate}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 38, y: 160 },
            width: 62,
            height: 6,
            content: "Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 104, y: 160 },
            width: 86,
            height: 6,
            content: "{{Weight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 38, y: 174 },
            width: 62,
            height: 6,
            content: "Total Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 104, y: 174 },
            width: 86,
            height: 6,
            content: "{{TotalWeight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 38, y: 185 },
            width: 62,
            height: 6,
            content: "Total Amount",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 104, y: 185 },
            width: 86,
            height: 6,
            content: "₹ {{ClosingBalance}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 38, y: 196 },
            width: 62,
            height: 6,
            content: "Old Balance",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 104, y: 196 },
            width: 86,
            height: 6,
            content: "₹ {{OldBalance}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 125, y: 245 },
            width: 65,
            height: 7,
            content: "Receiver Signature",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 10. A5 CASH / BANK PAYMENT VOUCHER (PORTRAIT PASSBOOK FORMAT)
  // =======================================================================================
  a5Payment: {
    id: "a5Payment",
    name: "A5 Cash / Bank Payment Voucher",
    paperSpec: "A5 Sheet / Passbook (148 × 210 mm)",
    voucherType: "PAYMENT",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 132,
            height: 194,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 8, y: 16 },
            width: 132,
            height: 7,
            content: "PAYMENT",
            fontSize: 15,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 8, y: 23.5 },
            width: 132,
            height: 6,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 13, y: 35 },
            width: 12,
            height: 5,
            content: "No.",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 26, y: 35 },
            width: 44,
            height: 5,
            content: "{{VoucherNo}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 80, y: 35 },
            width: 52,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 9.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 26, y: 46 },
            width: 46,
            height: 5,
            content: "Account Number",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 74, y: 46 },
            width: 58,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 26, y: 56 },
            width: 46,
            height: 5,
            content: "Paid To",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 74, y: 56 },
            width: 58,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 26, y: 66 },
            width: 46,
            height: 5,
            content: "The sum of rupees",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 74, y: 66 },
            width: 58,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 9,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 26, y: 76 },
            width: 46,
            height: 5,
            content: "By Cash / cheque/DD No",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 74, y: 76 },
            width: 58,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 26, y: 88 },
            width: 13,
            height: 8.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 26, y: 89 },
            width: 13,
            height: 7,
            content: "₹",
            fontSize: 12,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 39, y: 88 },
            width: 54,
            height: 8.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 41, y: 89.5 },
            width: 49,
            height: 6,
            content: "{{TotalAmount}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 26, y: 98.5 },
            width: 75,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 7,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 26, y: 108 },
            width: 46,
            height: 5,
            content: "Rate/Gm",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 74, y: 108 },
            width: 58,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 26, y: 118 },
            width: 46,
            height: 5,
            content: "Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 74, y: 118 },
            width: 58,
            height: 5,
            content: "{{Weight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 26, y: 128 },
            width: 46,
            height: 5,
            content: "Total Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 74, y: 128 },
            width: 58,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 26, y: 136 },
            width: 46,
            height: 5,
            content: "Total Amount",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 74, y: 136 },
            width: 58,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 26, y: 144 },
            width: 46,
            height: 5,
            content: "Old Balance",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 74, y: 144 },
            width: 58,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 72, y: 178 },
            width: 60,
            height: 6,
            content: "Receiver Signature",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 11. A5 LANDSCAPE / HALF-A4 PAYMENT VOUCHER (SHERSOFT ERP EXACT REPLICA)
  // =======================================================================================
  a5LandscapePayment: {
    id: "a5LandscapePayment",
    name: "A5 Landscape / Half-A4 Payment Voucher",
    paperSpec: "Half-A4 Sheet (210 × 148 mm)",
    voucherType: "PAYMENT",
    width: 210,
    height: 148,
    template: {
      basePdf: {
        width: 210,
        height: 148,
        padding: [8, 8, 8, 8]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 194,
            height: 132,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 8, y: 14 },
            width: 194,
            height: 6.5,
            content: "PAYMENT",
            fontSize: 15,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 8, y: 21 },
            width: 194,
            height: 5.5,
            content: "{{CompanyName}}",
            fontSize: 12,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 16, y: 31 },
            width: 14,
            height: 5,
            content: "No.",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 36, y: 31 },
            width: 50,
            height: 5,
            content: "{{VoucherNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 140, y: 31 },
            width: 56,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 36, y: 40 },
            width: 55,
            height: 5,
            content: "Account Number",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 95, y: 40 },
            width: 65,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 36, y: 48.5 },
            width: 55,
            height: 5,
            content: "Paid To",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 95, y: 48.5 },
            width: 65,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 36, y: 57 },
            width: 55,
            height: 5,
            content: "The sum of rupees",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 95, y: 57 },
            width: 65,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 36, y: 65.5 },
            width: 55,
            height: 5,
            content: "By Cash / cheque/DD No",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 95, y: 65.5 },
            width: 65,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 36, y: 75 },
            width: 14,
            height: 8.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 36, y: 76 },
            width: 14,
            height: 7,
            content: "₹",
            fontSize: 12,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 50, y: 75 },
            width: 58,
            height: 8.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 52, y: 76.5 },
            width: 53,
            height: 6,
            content: "{{TotalAmount}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 36, y: 85 },
            width: 75,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 36, y: 93 },
            width: 55,
            height: 5,
            content: "Rate/Gm",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 95, y: 93 },
            width: 65,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 36, y: 101.5 },
            width: 55,
            height: 5,
            content: "Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 95, y: 101.5 },
            width: 65,
            height: 5,
            content: "{{Weight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 36, y: 110 },
            width: 55,
            height: 5,
            content: "Total Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 95, y: 110 },
            width: 65,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 36, y: 117 },
            width: 55,
            height: 5,
            content: "Total Amount",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 95, y: 117 },
            width: 65,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 36, y: 124 },
            width: 55,
            height: 5,
            content: "Old Balance",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 95, y: 124 },
            width: 65,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 135, y: 132 },
            width: 60,
            height: 6,
            content: "Receiver Signature",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 12. 80MM POS THERMAL RECEIPT VOUCHER (SHERSOFT ERP)
  // =======================================================================================
  thermal80mmReceipt: {
    id: "thermal80mmReceipt",
    name: "80mm POS Thermal Receipt Voucher",
    paperSpec: "80mm POS Thermal (80 × 150 mm)",
    voucherType: "RECEIPT",
    width: 80,
    height: 150,
    template: {
      basePdf: {
        width: 80,
        height: 150,
        padding: [3, 3, 3, 3]
      },
      schemas: [
        [
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 3, y: 5 },
            width: 74,
            height: 6,
            content: "RECEIPT",
            fontSize: 13,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 3, y: 11.5 },
            width: 74,
            height: 5,
            content: "{{CompanyName}}",
            fontSize: 10.5,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 3, y: 20 },
            width: 36,
            height: 5,
            content: "No.{{VoucherNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 39, y: 20 },
            width: 38,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 3, y: 28 },
            width: 36,
            height: 5,
            content: "Account Number",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 39, y: 28 },
            width: 38,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 3, y: 35 },
            width: 36,
            height: 5,
            content: "Received From",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 39, y: 35 },
            width: 38,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 3, y: 42 },
            width: 34,
            height: 5,
            content: "The sum of rupees",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 37, y: 42 },
            width: 40,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 3, y: 49 },
            width: 36,
            height: 8,
            content: "By Cash /\ncheque/DD No",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 39, y: 50 },
            width: 38,
            height: 6,
            content: "{{PaymentMode}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 3, y: 59 },
            width: 9,
            height: 7.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 3, y: 59.5 },
            width: 9,
            height: 6,
            content: "₹",
            fontSize: 10,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 12, y: 59 },
            width: 34,
            height: 7.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 13, y: 60.5 },
            width: 31,
            height: 5.5,
            content: "{{TotalAmount}}",
            fontSize: 9.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 3, y: 68 },
            width: 74,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 6,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 3, y: 74 },
            width: 36,
            height: 5,
            content: "Rate/Gm",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 39, y: 74 },
            width: 38,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 3, y: 81 },
            width: 36,
            height: 5,
            content: "Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 39, y: 81 },
            width: 38,
            height: 5,
            content: "{{Weight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 3, y: 88 },
            width: 36,
            height: 5,
            content: "Total Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 39, y: 88 },
            width: 38,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 3, y: 95 },
            width: 36,
            height: 5,
            content: "Total Amount",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 39, y: 95 },
            width: 38,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 3, y: 102 },
            width: 36,
            height: 5,
            content: "Old Balance",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 39, y: 102 },
            width: 38,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 20, y: 114 },
            width: 57,
            height: 5,
            content: "Receiver Signature",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 13. 58MM POS THERMAL RECEIPT VOUCHER (SHERSOFT ERP)
  // =======================================================================================
  thermal58mmReceipt: {
    id: "thermal58mmReceipt",
    name: "58mm POS Thermal Receipt Voucher",
    paperSpec: "58mm POS Thermal (58 × 130 mm)",
    voucherType: "RECEIPT",
    width: 58,
    height: 130,
    template: {
      basePdf: {
        width: 58,
        height: 130,
        padding: [2, 2, 2, 2]
      },
      schemas: [
        [
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 2, y: 4 },
            width: 54,
            height: 5,
            content: "RECEIPT",
            fontSize: 11,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 2, y: 9.5 },
            width: 54,
            height: 4.5,
            content: "{{CompanyName}}",
            fontSize: 9,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 2, y: 16 },
            width: 26,
            height: 4,
            content: "No.{{VoucherNo}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 28, y: 16 },
            width: 28,
            height: 4,
            content: "Date:{{Date}}",
            fontSize: 7.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 2, y: 22 },
            width: 26,
            height: 4,
            content: "Account Number",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 28, y: 22 },
            width: 28,
            height: 4,
            content: "{{AccountNo}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 2, y: 27.5 },
            width: 26,
            height: 4,
            content: "Received From",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 28, y: 27.5 },
            width: 28,
            height: 4,
            content: "{{CustomerName}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 2, y: 33 },
            width: 26,
            height: 4,
            content: "The sum of rupees",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 28, y: 33 },
            width: 28,
            height: 4,
            content: "{{AmountInWords}}",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 2, y: 38.5 },
            width: 26,
            height: 7,
            content: "By Cash /\ncheque/DD No",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 28, y: 39.5 },
            width: 28,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 2, y: 47 },
            width: 8,
            height: 6.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 2, y: 47.5 },
            width: 8,
            height: 5,
            content: "₹",
            fontSize: 9,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 10, y: 47 },
            width: 28,
            height: 6.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 11, y: 48 },
            width: 25,
            height: 5,
            content: "{{TotalAmount}}",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 2, y: 55 },
            width: 54,
            height: 3.5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 5.2,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 2, y: 60 },
            width: 26,
            height: 4,
            content: "Rate/Gm",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 28, y: 60 },
            width: 28,
            height: 4,
            content: "₹ {{Rate}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 2, y: 65.5 },
            width: 26,
            height: 4,
            content: "Weight",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 28, y: 65.5 },
            width: 28,
            height: 4,
            content: "{{Weight}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 2, y: 71 },
            width: 26,
            height: 4,
            content: "Total Weight",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 28, y: 71 },
            width: 28,
            height: 4,
            content: "{{TotalWeight}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 2, y: 76.5 },
            width: 26,
            height: 4,
            content: "Total Amount",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 28, y: 76.5 },
            width: 28,
            height: 4,
            content: "₹ {{ClosingBalance}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 2, y: 82 },
            width: 26,
            height: 4,
            content: "Old Balance",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 28, y: 82 },
            width: 28,
            height: 4,
            content: "₹ {{OldBalance}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 12, y: 92 },
            width: 44,
            height: 5,
            content: "Receiver Signature",
            fontSize: 7.5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 14. 80MM POS THERMAL PAYMENT VOUCHER (SHERSOFT ERP)
  // =======================================================================================
  thermal80mmPayment: {
    id: "thermal80mmPayment",
    name: "80mm POS Thermal Payment Voucher",
    paperSpec: "80mm POS Thermal (80 × 150 mm)",
    voucherType: "PAYMENT",
    width: 80,
    height: 150,
    template: {
      basePdf: {
        width: 80,
        height: 150,
        padding: [3, 3, 3, 3]
      },
      schemas: [
        [
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 3, y: 5 },
            width: 74,
            height: 6,
            content: "PAYMENT",
            fontSize: 13,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 3, y: 11.5 },
            width: 74,
            height: 5,
            content: "{{CompanyName}}",
            fontSize: 10.5,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 3, y: 20 },
            width: 36,
            height: 5,
            content: "No.{{VoucherNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 39, y: 20 },
            width: 38,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 3, y: 28 },
            width: 36,
            height: 5,
            content: "Account Number",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 39, y: 28 },
            width: 38,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 3, y: 35 },
            width: 36,
            height: 5,
            content: "Paid To",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 39, y: 35 },
            width: 38,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 3, y: 42 },
            width: 34,
            height: 5,
            content: "The sum of rupees",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 37, y: 42 },
            width: 40,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 3, y: 49 },
            width: 36,
            height: 8,
            content: "By Cash /\ncheque/DD No",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 39, y: 50 },
            width: 38,
            height: 6,
            content: "{{PaymentMode}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 3, y: 59 },
            width: 9,
            height: 7.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 3, y: 59.5 },
            width: 9,
            height: 6,
            content: "₹",
            fontSize: 10,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 12, y: 59 },
            width: 34,
            height: 7.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 13, y: 60.5 },
            width: 31,
            height: 5.5,
            content: "{{TotalAmount}}",
            fontSize: 9.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 3, y: 68 },
            width: 74,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 6,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 3, y: 74 },
            width: 36,
            height: 5,
            content: "Rate/Gm",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 39, y: 74 },
            width: 38,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 3, y: 81 },
            width: 36,
            height: 5,
            content: "Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 39, y: 81 },
            width: 38,
            height: 5,
            content: "{{Weight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 3, y: 88 },
            width: 36,
            height: 5,
            content: "Total Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 39, y: 88 },
            width: 38,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 3, y: 95 },
            width: 36,
            height: 5,
            content: "Total Amount",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 39, y: 95 },
            width: 38,
            height: 5,
            content: "₹ {{ClosingBalance}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 3, y: 102 },
            width: 36,
            height: 5,
            content: "Old Balance",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 39, y: 102 },
            width: 38,
            height: 5,
            content: "₹ {{OldBalance}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 20, y: 114 },
            width: 57,
            height: 5,
            content: "Receiver Signature",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 15. 58MM POS THERMAL PAYMENT VOUCHER (SHERSOFT ERP)
  // =======================================================================================
  thermal58mmPayment: {
    id: "thermal58mmPayment",
    name: "58mm POS Thermal Payment Voucher",
    paperSpec: "58mm POS Thermal (58 × 130 mm)",
    voucherType: "PAYMENT",
    width: 58,
    height: 130,
    template: {
      basePdf: {
        width: 58,
        height: 130,
        padding: [2, 2, 2, 2]
      },
      schemas: [
        [
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 2, y: 4 },
            width: 54,
            height: 5,
            content: "PAYMENT",
            fontSize: 11,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 2, y: 9.5 },
            width: 54,
            height: 4.5,
            content: "{{CompanyName}}",
            fontSize: 9,
            alignment: "center",
            fontColor: "#000000"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 2, y: 16 },
            width: 26,
            height: 4,
            content: "No.{{VoucherNo}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 28, y: 16 },
            width: 28,
            height: 4,
            content: "Date:{{Date}}",
            fontSize: 7.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 2, y: 22 },
            width: 26,
            height: 4,
            content: "Account Number",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 28, y: 22 },
            width: 28,
            height: 4,
            content: "{{AccountNo}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 2, y: 27.5 },
            width: 26,
            height: 4,
            content: "Paid To",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 28, y: 27.5 },
            width: 28,
            height: 4,
            content: "{{CustomerName}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 2, y: 33 },
            width: 26,
            height: 4,
            content: "The sum of rupees",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 28, y: 33 },
            width: 28,
            height: 4,
            content: "{{AmountInWords}}",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 2, y: 38.5 },
            width: 26,
            height: 7,
            content: "By Cash /\ncheque/DD No",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 28, y: 39.5 },
            width: 28,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 2, y: 47 },
            width: 8,
            height: 6.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 2, y: 47.5 },
            width: 8,
            height: 5,
            content: "₹",
            fontSize: 9,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 10, y: 47 },
            width: 28,
            height: 6.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 11, y: 48 },
            width: 25,
            height: 5,
            content: "{{TotalAmount}}",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 2, y: 55 },
            width: 54,
            height: 3.5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 5.2,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 2, y: 60 },
            width: 26,
            height: 4,
            content: "Rate/Gm",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 28, y: 60 },
            width: 28,
            height: 4,
            content: "₹ {{Rate}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 2, y: 65.5 },
            width: 26,
            height: 4,
            content: "Weight",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 28, y: 65.5 },
            width: 28,
            height: 4,
            content: "{{Weight}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 2, y: 71 },
            width: 26,
            height: 4,
            content: "Total Weight",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 28, y: 71 },
            width: 28,
            height: 4,
            content: "{{TotalWeight}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblClosingBalance",
            type: "text",
            position: { x: 2, y: 76.5 },
            width: 26,
            height: 4,
            content: "Total Amount",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valClosingBalance",
            type: "text",
            position: { x: 28, y: 76.5 },
            width: 28,
            height: 4,
            content: "₹ {{ClosingBalance}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblOldBalance",
            type: "text",
            position: { x: 2, y: 82 },
            width: 26,
            height: 4,
            content: "Old Balance",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valOldBalance",
            type: "text",
            position: { x: 28, y: 82 },
            width: 28,
            height: 4,
            content: "₹ {{OldBalance}}",
            fontSize: 7.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 12, y: 92 },
            width: 44,
            height: 5,
            content: "Receiver Signature",
            fontSize: 7.5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 17. 58MM POS SMART RECEIPT VOUCHER (LOGO, UPI QR & WEBSITE)
  // =======================================================================================
  // Calibrated for standard 58mm POS thermal printers (accounting for hardware 4-5mm left offset)
  thermal58mmReceiptSmart: {
    id: "thermal58mmReceiptSmart",
    name: "58mm POS Smart Receipt (Logo & UPI QR)",
    paperSpec: "58mm POS Thermal (58 × 165 mm)",
    voucherType: "RECEIPT",
    width: 58,
    height: 165,
    template: {
      basePdf: {
        width: 58,
        height: 165,
        padding: [2, 1, 2, 1]
      },
      schemas: [
        [
          // Showroom Brand Logo (Calibrated centered for hardware offset)
          {
            name: "showroomLogo",
            type: "image",
            position: { x: 9, y: 3 },
            width: 28,
            height: 11,
            content: DEFAULT_PRESET_LOGO
          },
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 0, y: 15.5 },
            width: 46,
            height: 4.5,
            content: "RECEIPT",
            fontSize: 10,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 0, y: 20.5 },
            width: 46,
            height: 4,
            content: "{{CompanyName}}",
            fontSize: 8.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "showroomWebsite",
            type: "text",
            position: { x: 0, y: 25 },
            width: 46,
            height: 3.5,
            content: "Web: {{ShowroomWebsite}}",
            fontSize: 6.2,
            alignment: "center",
            fontColor: "#475569"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 1, y: 30 },
            width: 22,
            height: 4,
            content: "No.{{VoucherNo}}",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 22, y: 30 },
            width: 24,
            height: 4,
            content: "Date:{{Date}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 1, y: 35.5 },
            width: 22,
            height: 4,
            content: "Account Number",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 23, y: 35.5 },
            width: 23,
            height: 4,
            content: "{{AccountNo}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 1, y: 41 },
            width: 20,
            height: 4,
            content: "Received From",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 21, y: 41 },
            width: 25,
            height: 4,
            content: "{{CustomerName}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 1, y: 46.5 },
            width: 22,
            height: 4,
            content: "The sum of rupees",
            fontSize: 6.8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 23, y: 46.5 },
            width: 23,
            height: 4,
            content: "{{AmountInWords}}",
            fontSize: 6.8,
            alignment: "right",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 1, y: 52 },
            width: 20,
            height: 7,
            content: "By Cash /\ncheque/DD No",
            fontSize: 6.8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 21, y: 53 },
            width: 25,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 1, y: 60.5 },
            width: 7,
            height: 6,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 1, y: 61 },
            width: 7,
            height: 5,
            content: "₹",
            fontSize: 8.5,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 8, y: 60.5 },
            width: 24,
            height: 6,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 9, y: 61.5 },
            width: 22,
            height: 5,
            content: "{{TotalAmount}}",
            fontSize: 8,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 1, y: 68 },
            width: 45,
            height: 3.5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 4.8,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 1, y: 73 },
            width: 20,
            height: 4,
            content: "Rate/Gm",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 21, y: 73 },
            width: 25,
            height: 4,
            content: "₹ {{Rate}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 1, y: 78 },
            width: 20,
            height: 4,
            content: "Weight",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 21, y: 78 },
            width: 25,
            height: 4,
            content: "{{Weight}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 1, y: 83 },
            width: 20,
            height: 4,
            content: "Total Weight",
            fontSize: 7,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 21, y: 83 },
            width: 25,
            height: 4,
            content: "{{TotalWeight}}",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          },

          // UPI Payment QR Code
          {
            name: "qrCode",
            type: "qrcode",
            position: { x: 11, y: 90 },
            width: 24,
            height: 24,
            content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR"
          },
          {
            name: "qrCaption",
            type: "text",
            position: { x: 0, y: 115.5 },
            width: 46,
            height: 3.5,
            content: "Scan with GPay / PhonePe / Paytm",
            fontSize: 5.2,
            alignment: "center",
            fontColor: "#475569"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 6, y: 121.5 },
            width: 40,
            height: 5,
            content: "Receiver Signature",
            fontSize: 7,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 18. 80MM POS SMART RECEIPT VOUCHER (LOGO, UPI QR & WEBSITE)
  // =======================================================================================
  thermal80mmReceiptSmart: {
    id: "thermal80mmReceiptSmart",
    name: "80mm POS Smart Receipt (Logo & UPI QR)",
    paperSpec: "80mm POS Thermal (80 × 175 mm)",
    voucherType: "RECEIPT",
    width: 80,
    height: 175,
    template: {
      basePdf: {
        width: 80,
        height: 175,
        padding: [3, 3, 3, 3]
      },
      schemas: [
        [
          // Showroom Brand Logo
          {
            name: "showroomLogo",
            type: "image",
            position: { x: 25, y: 4 },
            width: 30,
            height: 13,
            content: DEFAULT_PRESET_LOGO
          },
          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 3, y: 18.5 },
            width: 74,
            height: 5.5,
            content: "RECEIPT",
            fontSize: 12,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 3, y: 24.5 },
            width: 74,
            height: 4.5,
            content: "{{CompanyName}}",
            fontSize: 10,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "showroomWebsite",
            type: "text",
            position: { x: 3, y: 29.5 },
            width: 74,
            height: 3.5,
            content: "Web: {{ShowroomWebsite}}",
            fontSize: 7,
            alignment: "center",
            fontColor: "#334155"
          },

          // Subheader: Voucher No & Date
          {
            name: "valNo",
            type: "text",
            position: { x: 3, y: 35 },
            width: 36,
            height: 5,
            content: "No.{{VoucherNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 39, y: 35 },
            width: 38,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 3, y: 42 },
            width: 36,
            height: 5,
            content: "Account Number",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 39, y: 42 },
            width: 38,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 3, y: 48 },
            width: 36,
            height: 5,
            content: "Received From",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 39, y: 48 },
            width: 38,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 3, y: 54 },
            width: 36,
            height: 5,
            content: "The sum of rupees",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 39, y: 54 },
            width: 38,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 3, y: 60 },
            width: 36,
            height: 7,
            content: "By Cash /\ncheque/DD No",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 39, y: 61 },
            width: 38,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 3, y: 69 },
            width: 10,
            height: 7.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 3, y: 69.5 },
            width: 10,
            height: 6,
            content: "₹",
            fontSize: 11,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 13, y: 69 },
            width: 36,
            height: 7.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 14, y: 70.5 },
            width: 33,
            height: 5.5,
            content: "{{TotalAmount}}",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 3, y: 78 },
            width: 74,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 6,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 3, y: 84 },
            width: 36,
            height: 5,
            content: "Rate/Gm",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 39, y: 84 },
            width: 38,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 3, y: 91 },
            width: 36,
            height: 5,
            content: "Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 39, y: 91 },
            width: 38,
            height: 5,
            content: "{{Weight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 3, y: 98 },
            width: 36,
            height: 5,
            content: "Total Weight",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 39, y: 98 },
            width: 38,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // UPI Payment QR Code
          {
            name: "qrCode",
            type: "qrcode",
            position: { x: 27, y: 106 },
            width: 26,
            height: 26,
            content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR"
          },
          {
            name: "qrCaption",
            type: "text",
            position: { x: 3, y: 133.5 },
            width: 74,
            height: 3.5,
            content: "Scan with GPay / PhonePe / Paytm",
            fontSize: 6.5,
            alignment: "center",
            fontColor: "#475569"
          },

          // Receiver Signature at Bottom
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 20, y: 140 },
            width: 57,
            height: 5,
            content: "Receiver Signature",
            fontSize: 8.5,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 19. A5 SMART RECEIPT VOUCHER (LOGO, UPI QR & WEBSITE)
  // =======================================================================================
  a5ReceiptSmart: {
    id: "a5ReceiptSmart",
    name: "A5 Smart Receipt Voucher (Logo & UPI QR)",
    paperSpec: "A5 Sheet / Passbook (148 × 210 mm)",
    voucherType: "RECEIPT",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 8, y: 8 },
            width: 132,
            height: 194,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Showroom Brand Logo
          {
            name: "showroomLogo",
            type: "image",
            position: { x: 56, y: 12 },
            width: 36,
            height: 15,
            content: DEFAULT_PRESET_LOGO
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 8, y: 28.5 },
            width: 132,
            height: 6.5,
            content: "RECEIPT",
            fontSize: 14,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 8, y: 35.5 },
            width: 132,
            height: 5.5,
            content: "{{CompanyName}}",
            fontSize: 11.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "showroomWebsite",
            type: "text",
            position: { x: 8, y: 41.5 },
            width: 132,
            height: 4,
            content: "Web: {{ShowroomWebsite}}",
            fontSize: 7.5,
            alignment: "center",
            fontColor: "#334155"
          },

          // Header Divider Line
          {
            name: "dividerLine",
            type: "line",
            position: { x: 13, y: 46.5 },
            width: 122,
            height: 0.3,
            color: "#cbd5e1"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 13, y: 49.5 },
            width: 12,
            height: 5,
            content: "No.",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 26, y: 49.5 },
            width: 44,
            height: 5,
            content: "{{VoucherNo}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 80, y: 49.5 },
            width: 52,
            height: 5,
            content: "Date:{{Date}}",
            fontSize: 9.5,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 26, y: 58.5 },
            width: 46,
            height: 5,
            content: "Account Number",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 74, y: 58.5 },
            width: 58,
            height: 5,
            content: "{{AccountNo}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 26, y: 67.5 },
            width: 46,
            height: 5,
            content: "Received From",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 74, y: 67.5 },
            width: 58,
            height: 5,
            content: "{{CustomerName}}",
            fontSize: 10,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 26, y: 76.5 },
            width: 46,
            height: 5,
            content: "The sum of rupees",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 74, y: 76.5 },
            width: 58,
            height: 5,
            content: "{{AmountInWords}}",
            fontSize: 9,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 26, y: 85.5 },
            width: 46,
            height: 5,
            content: "By Cash / cheque/DD No",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 74, y: 85.5 },
            width: 58,
            height: 5,
            content: "{{PaymentMode}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 26, y: 95.5 },
            width: 13,
            height: 8.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 26, y: 96.5 },
            width: 13,
            height: 7,
            content: "₹",
            fontSize: 12,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 39, y: 95.5 },
            width: 54,
            height: 8.5,
            borderWidth: 0.5,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 41, y: 97 },
            width: 49,
            height: 6,
            content: "{{TotalAmount}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 26, y: 105.5 },
            width: 75,
            height: 4,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 7,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 26, y: 113.5 },
            width: 46,
            height: 5,
            content: "Rate/Gm",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 74, y: 113.5 },
            width: 58,
            height: 5,
            content: "₹ {{Rate}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 26, y: 122.5 },
            width: 46,
            height: 5,
            content: "Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 74, y: 122.5 },
            width: 58,
            height: 5,
            content: "{{Weight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 26, y: 131.5 },
            width: 46,
            height: 5,
            content: "Total Weight",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 74, y: 131.5 },
            width: 58,
            height: 5,
            content: "{{TotalWeight}}",
            fontSize: 9.5,
            alignment: "left",
            fontColor: "#000000"
          },

          // UPI Payment QR Code & Caption (Bottom Left)
          {
            name: "qrCode",
            type: "qrcode",
            position: { x: 26, y: 142 },
            width: 25,
            height: 25,
            content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR"
          },
          {
            name: "qrCaption",
            type: "text",
            position: { x: 19, y: 168.5 },
            width: 39,
            height: 3.5,
            content: "Scan QR to Pay via UPI",
            fontSize: 6.5,
            alignment: "center",
            fontColor: "#475569"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 74, y: 168.5 },
            width: 58,
            height: 6,
            content: "Receiver Signature",
            fontSize: 10,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 20. A4 SMART RECEIPT VOUCHER (LOGO, UPI QR & WEBSITE)
  // =======================================================================================
  a4ReceiptSmart: {
    id: "a4ReceiptSmart",
    name: "A4 Smart Receipt Voucher (Logo & UPI QR)",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "RECEIPT",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [10, 10, 10, 10]
      },
      schemas: [
        [
          // Outer Protective Border
          {
            name: "outerBorder",
            type: "rectangle",
            position: { x: 10, y: 10 },
            width: 190,
            height: 277,
            borderWidth: 0.8,
            borderColor: "#000000"
          },

          // Showroom Brand Logo
          {
            name: "showroomLogo",
            type: "image",
            position: { x: 85, y: 15 },
            width: 40,
            height: 17,
            content: DEFAULT_PRESET_LOGO
          },

          // Centered Header
          {
            name: "voucherTitle",
            type: "text",
            position: { x: 10, y: 34 },
            width: 190,
            height: 8,
            content: "RECEIPT",
            fontSize: 18,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "companyName",
            type: "text",
            position: { x: 10, y: 43 },
            width: 190,
            height: 7,
            content: "{{CompanyName}}",
            fontSize: 13.5,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "showroomWebsite",
            type: "text",
            position: { x: 10, y: 50.5 },
            width: 190,
            height: 4.5,
            content: "Web: {{ShowroomWebsite}}",
            fontSize: 8.5,
            alignment: "center",
            fontColor: "#334155"
          },

          // Header Divider Line
          {
            name: "dividerLine",
            type: "line",
            position: { x: 20, y: 56.5 },
            width: 170,
            height: 0.4,
            color: "#cbd5e1"
          },

          // Subheader: Voucher No & Date
          {
            name: "lblNo",
            type: "text",
            position: { x: 20, y: 60 },
            width: 15,
            height: 6,
            content: "No.",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valNo",
            type: "text",
            position: { x: 38, y: 60 },
            width: 55,
            height: 6,
            content: "{{VoucherNo}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valDate",
            type: "text",
            position: { x: 130, y: 60 },
            width: 60,
            height: 6,
            content: "Date:{{Date}}",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          },

          // Transaction Details Rows
          {
            name: "lblAccountNo",
            type: "text",
            position: { x: 38, y: 72 },
            width: 62,
            height: 6,
            content: "Account Number",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valAccountNo",
            type: "text",
            position: { x: 104, y: 72 },
            width: 86,
            height: 6,
            content: "{{AccountNo}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblParty",
            type: "text",
            position: { x: 38, y: 84 },
            width: 62,
            height: 6,
            content: "Received From",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valParty",
            type: "text",
            position: { x: 104, y: 84 },
            width: 86,
            height: 6,
            content: "{{CustomerName}}",
            fontSize: 11.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblSumOfRupees",
            type: "text",
            position: { x: 38, y: 96 },
            width: 62,
            height: 6,
            content: "The sum of rupees",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valSumOfRupees",
            type: "text",
            position: { x: 104, y: 96 },
            width: 86,
            height: 6,
            content: "{{AmountInWords}}",
            fontSize: 10.5,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblPaymentMode",
            type: "text",
            position: { x: 38, y: 108 },
            width: 62,
            height: 6,
            content: "By Cash / cheque/DD No",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valPaymentMode",
            type: "text",
            position: { x: 104, y: 108 },
            width: 86,
            height: 6,
            content: "{{PaymentMode}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // Currency Box
          {
            name: "boxRupeeBg",
            type: "rectangle",
            position: { x: 38, y: 121 },
            width: 16,
            height: 10.5,
            borderWidth: 0,
            color: "#000000"
          },
          {
            name: "lblRupeeSign",
            type: "text",
            position: { x: 38, y: 122 },
            width: 16,
            height: 8.5,
            content: "₹",
            fontSize: 15,
            alignment: "center",
            fontColor: "#ffffff"
          },
          {
            name: "boxAmountBorder",
            type: "rectangle",
            position: { x: 54, y: 121 },
            width: 70,
            height: 10.5,
            borderWidth: 0.6,
            borderColor: "#000000",
            color: "#ffffff"
          },
          {
            name: "valTotalAmount",
            type: "text",
            position: { x: 56, y: 123 },
            width: 64,
            height: 7.5,
            content: "{{TotalAmount}}",
            fontSize: 13,
            alignment: "right",
            fontColor: "#000000"
          },
          {
            name: "lblChequeRealization",
            type: "text",
            position: { x: 38, y: 133 },
            width: 100,
            height: 5,
            content: "* All Cheque / DD are subject to realization",
            fontSize: 8.5,
            alignment: "left",
            fontColor: "#333333"
          },

          // Gold / Bullion Rate & Weight Breakdown
          {
            name: "lblRate",
            type: "text",
            position: { x: 38, y: 144 },
            width: 62,
            height: 6,
            content: "Rate/Gm",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valRate",
            type: "text",
            position: { x: 104, y: 144 },
            width: 86,
            height: 6,
            content: "₹ {{Rate}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblWeight",
            type: "text",
            position: { x: 38, y: 155 },
            width: 62,
            height: 6,
            content: "Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valWeight",
            type: "text",
            position: { x: 104, y: 155 },
            width: 86,
            height: 6,
            content: "{{Weight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          {
            name: "lblTotalWeight",
            type: "text",
            position: { x: 38, y: 166 },
            width: 62,
            height: 6,
            content: "Total Weight",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },
          {
            name: "valTotalWeight",
            type: "text",
            position: { x: 104, y: 166 },
            width: 86,
            height: 6,
            content: "{{TotalWeight}}",
            fontSize: 11,
            alignment: "left",
            fontColor: "#000000"
          },

          // UPI Payment QR Code & Caption (Bottom Left)
          {
            name: "qrCode",
            type: "qrcode",
            position: { x: 38, y: 184 },
            width: 34,
            height: 34,
            content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR"
          },
          {
            name: "qrCaption",
            type: "text",
            position: { x: 30, y: 220 },
            width: 50,
            height: 4,
            content: "Scan with GPay / PhonePe / Paytm",
            fontSize: 7,
            alignment: "center",
            fontColor: "#475569"
          },

          // Receiver Signature at Bottom Right
          {
            name: "lblReceiverSignature",
            type: "text",
            position: { x: 120, y: 220 },
            width: 65,
            height: 7,
            content: "Receiver Signature",
            fontSize: 11,
            alignment: "right",
            fontColor: "#000000"
          }
        ]
      ]
    }
  },

  // =======================================================================================
  // 21. 58MM POS MODERN EXECUTIVE RECEIPT (LOGO, HERO AMOUNT & BULLION CARD)
  // =======================================================================================
  // Calibrated for standard 58mm thermal rolls (48mm print head with 5-6mm safe hardware margin)
  thermal58mmReceiptModern: {
    id: "thermal58mmReceiptModern",
    name: "58mm Modern Executive Receipt",
    paperSpec: "58mm POS Thermal Roll (58 × 165 mm)",
    voucherType: "RECEIPT",
    width: 58,
    height: 165,
    template: {
      basePdf: {
        width: 58,
        height: 165,
        padding: [2, 6, 2, 6]
      },
      schemas: [
        [
          { name: "showroomLogo", type: "image", position: { x: 19, y: 3 }, width: 20, height: 10, content: DEFAULT_PRESET_LOGO },
          { name: "companyName", type: "text", position: { x: 6, y: 14 }, width: 46, height: 4.5, content: "{{CompanyName}}", fontSize: 9, alignment: "center", fontColor: "#0f2d59" },
          { name: "showroomWebsite", type: "text", position: { x: 6, y: 19 }, width: 46, height: 3.5, content: "Web: {{ShowroomWebsite}}", fontSize: 6.2, alignment: "center", fontColor: "#475569" },
          { name: "titleBarBg", type: "rectangle", position: { x: 6, y: 23.5 }, width: 46, height: 5, color: "#0f2d59", borderWidth: 0 },
          { name: "voucherTitle", type: "text", position: { x: 6, y: 24.2 }, width: 46, height: 4, content: "★ PAYMENT RECEIPT ★", fontSize: 7.5, alignment: "center", fontColor: "#ffffff" },
          
          { name: "metaBox", type: "rectangle", position: { x: 6, y: 30 }, width: 46, height: 8.5, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "lblNo", type: "text", position: { x: 7.5, y: 31 }, width: 14, height: 3.2, content: "Receipt #", fontSize: 5.8, fontColor: "#64748b" },
          { name: "valNo", type: "text", position: { x: 21.5, y: 31 }, width: 29, height: 3.2, content: "#{{VoucherNo}}", fontSize: 6.2, alignment: "left", fontColor: "#0f2d59" },
          { name: "lblDate", type: "text", position: { x: 7.5, y: 34.5 }, width: 14, height: 3.2, content: "Date:", fontSize: 5.8, fontColor: "#64748b" },
          { name: "valDate", type: "text", position: { x: 21.5, y: 34.5 }, width: 29, height: 3.2, content: "{{Date}}", fontSize: 6.2, alignment: "left", fontColor: "#000000" },

          { name: "custBox", type: "rectangle", position: { x: 6, y: 40 }, width: 46, height: 26, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "lblAccountNo", type: "text", position: { x: 7.5, y: 41.5 }, width: 20, height: 3.2, content: "Account No:", fontSize: 5.8, fontColor: "#64748b" },
          { name: "valAccountNo", type: "text", position: { x: 27.5, y: 41.5 }, width: 23, height: 3.2, content: "{{AccountNo}}", fontSize: 6.2, alignment: "right", fontColor: "#0f2d59" },
          { name: "lineCust1", type: "line", position: { x: 7.5, y: 45.2 }, width: 43, height: 0.2, color: "#e2e8f0" },
          { name: "lblCustomer", type: "text", position: { x: 7.5, y: 46.2 }, width: 43, height: 3, content: "RECEIVED WITH THANKS FROM:", fontSize: 5.2, fontColor: "#64748b" },
          { name: "valCustomer", type: "text", position: { x: 7.5, y: 49.5 }, width: 43, height: 4, content: "{{CustomerName}}", fontSize: 7, fontColor: "#000000" },
          { name: "lineCust2", type: "line", position: { x: 7.5, y: 54 }, width: 43, height: 0.2, color: "#e2e8f0" },
          { name: "lblPaymentMode", type: "text", position: { x: 7.5, y: 55 }, width: 20, height: 3.2, content: "Payment Mode:", fontSize: 5.8, fontColor: "#64748b" },
          { name: "valPaymentMode", type: "text", position: { x: 27.5, y: 55 }, width: 23, height: 3.2, content: "{{PaymentMode}}", fontSize: 6.2, alignment: "right", fontColor: "#000000" },
          { name: "valWords", type: "text", position: { x: 7.5, y: 59 }, width: 43, height: 6, content: "Sum: {{AmountInWords}}", fontSize: 5.5, fontColor: "#475569" },

          { name: "amountBannerBg", type: "rectangle", position: { x: 6, y: 68 }, width: 46, height: 11, color: "#0f2d59", borderWidth: 0 },
          { name: "lblTotalAmount", type: "text", position: { x: 6, y: 69 }, width: 46, height: 3, content: "TOTAL AMOUNT RECEIVED", fontSize: 5.2, alignment: "center", fontColor: "#fbbf24" },
          { name: "valTotalAmount", type: "text", position: { x: 6, y: 72.5 }, width: 46, height: 5.5, content: "₹ {{TotalAmount}}", fontSize: 10.5, alignment: "center", fontColor: "#ffffff" },
          { name: "disclaimerText", type: "text", position: { x: 6, y: 80 }, width: 46, height: 3, content: "* All Cheque / DD are subject to realization", fontSize: 4.8, alignment: "center", fontColor: "#64748b" },

          { name: "bullionBox", type: "rectangle", position: { x: 6, y: 84.5 }, width: 46, height: 16.5, borderWidth: 0.4, borderColor: "#d97706", color: "#fffbeb" },
          { name: "bullionHeader", type: "text", position: { x: 6, y: 85.5 }, width: 46, height: 3, content: "BULLION SAVINGS / WEIGHT LEDGER", fontSize: 5.2, alignment: "center", fontColor: "#b45309" },
          { name: "lblRate", type: "text", position: { x: 7.5, y: 89 }, width: 20, height: 3.2, content: "Rate / Gm:", fontSize: 5.8, fontColor: "#78350f" },
          { name: "valRate", type: "text", position: { x: 27.5, y: 89 }, width: 23, height: 3.2, content: "₹ {{Rate}}", fontSize: 6.2, alignment: "right", fontColor: "#78350f" },
          { name: "lblWeight", type: "text", position: { x: 7.5, y: 92.5 }, width: 20, height: 3.2, content: "Weight:", fontSize: 5.8, fontColor: "#78350f" },
          { name: "valWeight", type: "text", position: { x: 27.5, y: 92.5 }, width: 23, height: 3.2, content: "{{Weight}}", fontSize: 6.2, alignment: "right", fontColor: "#78350f" },
          { name: "lblTotalWeight", type: "text", position: { x: 7.5, y: 96 }, width: 20, height: 3.2, content: "Accumulated Weight:", fontSize: 5.8, fontColor: "#78350f" },
          { name: "valTotalWeight", type: "text", position: { x: 27.5, y: 96 }, width: 23, height: 3.2, content: "{{TotalWeight}}", fontSize: 6.2, alignment: "right", fontColor: "#78350f" },

          { name: "qrCode", type: "qrcode", position: { x: 17, y: 103 }, width: 24, height: 24, content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR" },
          { name: "qrCaption", type: "text", position: { x: 6, y: 128 }, width: 46, height: 3.2, content: "Scan with GPay / PhonePe / Paytm", fontSize: 5.2, alignment: "center", fontColor: "#475569" },
          { name: "thankYouMsg", type: "text", position: { x: 6, y: 132 }, width: 46, height: 3, content: "Thank you for your patronage!", fontSize: 5.8, alignment: "center", fontColor: "#0f2d59" },
          { name: "signLine", type: "line", position: { x: 12, y: 147 }, width: 34, height: 0.3, color: "#94a3b8" },
          { name: "lblReceiverSignature", type: "text", position: { x: 6, y: 148 }, width: 46, height: 4, content: "Authorized Signatory / Cashier", fontSize: 6.2, alignment: "center", fontColor: "#000000" }
        ]
      ]
    }
  },

  // =======================================================================================
  // 22. 80MM POS MODERN EXECUTIVE RECEIPT (LOGO, HERO AMOUNT & BULLION CARD)
  // =======================================================================================
  // Calibrated for standard 80mm thermal rolls (72mm print head with 6mm safe hardware margin)
  thermal80mmReceiptModern: {
    id: "thermal80mmReceiptModern",
    name: "80mm Modern Executive Receipt",
    paperSpec: "80mm POS Thermal Roll (80 × 175 mm)",
    voucherType: "RECEIPT",
    width: 80,
    height: 175,
    template: {
      basePdf: {
        width: 80,
        height: 175,
        padding: [3, 6, 3, 6]
      },
      schemas: [
        [
          { name: "showroomLogo", type: "image", position: { x: 28, y: 4 }, width: 24, height: 12, content: DEFAULT_PRESET_LOGO },
          { name: "companyName", type: "text", position: { x: 6, y: 17 }, width: 68, height: 5, content: "{{CompanyName}}", fontSize: 10.5, alignment: "center", fontColor: "#0f2d59" },
          { name: "showroomWebsite", type: "text", position: { x: 6, y: 22.5 }, width: 68, height: 3.5, content: "Web: {{ShowroomWebsite}}", fontSize: 6.8, alignment: "center", fontColor: "#475569" },
          { name: "titleBarBg", type: "rectangle", position: { x: 6, y: 27 }, width: 68, height: 6, color: "#0f2d59", borderWidth: 0 },
          { name: "voucherTitle", type: "text", position: { x: 6, y: 28 }, width: 68, height: 4.5, content: "★ OFFICIAL PAYMENT RECEIPT ★", fontSize: 8.5, alignment: "center", fontColor: "#ffffff" },

          { name: "metaBox", type: "rectangle", position: { x: 6, y: 34.5 }, width: 68, height: 9, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "lblNo", type: "text", position: { x: 8, y: 36 }, width: 18, height: 3.5, content: "Receipt No:", fontSize: 6.8, fontColor: "#64748b" },
          { name: "valNo", type: "text", position: { x: 26, y: 36 }, width: 18, height: 3.5, content: "#{{VoucherNo}}", fontSize: 7.2, fontColor: "#0f2d59" },
          { name: "lblDate", type: "text", position: { x: 44, y: 36 }, width: 10, height: 3.5, content: "Date:", fontSize: 6.8, fontColor: "#64748b" },
          { name: "valDate", type: "text", position: { x: 54, y: 36 }, width: 18, height: 3.5, content: "{{Date}}", fontSize: 7.2, alignment: "right", fontColor: "#000000" },
          { name: "metaNote", type: "text", position: { x: 8, y: 40 }, width: 64, height: 3, content: "Official Continuous Cash / Bank Receipt", fontSize: 5.2, alignment: "center", fontColor: "#94a3b8" },

          { name: "custBox", type: "rectangle", position: { x: 6, y: 45 }, width: 68, height: 28, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "lblAccountNo", type: "text", position: { x: 8, y: 47 }, width: 22, height: 3.5, content: "Account No:", fontSize: 6.8, fontColor: "#64748b" },
          { name: "valAccountNo", type: "text", position: { x: 30, y: 47 }, width: 42, height: 3.5, content: "{{AccountNo}}", fontSize: 7.2, alignment: "right", fontColor: "#0f2d59" },
          { name: "lineCust1", type: "line", position: { x: 8, y: 51.5 }, width: 64, height: 0.2, color: "#e2e8f0" },
          { name: "lblCustomer", type: "text", position: { x: 8, y: 52.5 }, width: 64, height: 3.2, content: "RECEIVED WITH THANKS FROM:", fontSize: 5.8, fontColor: "#64748b" },
          { name: "valCustomer", type: "text", position: { x: 8, y: 56 }, width: 64, height: 4.5, content: "{{CustomerName}}", fontSize: 8.2, fontColor: "#000000" },
          { name: "lineCust2", type: "line", position: { x: 8, y: 61.5 }, width: 64, height: 0.2, color: "#e2e8f0" },
          { name: "lblPaymentMode", type: "text", position: { x: 8, y: 62.5 }, width: 24, height: 3.5, content: "Payment Mode:", fontSize: 6.8, fontColor: "#64748b" },
          { name: "valPaymentMode", type: "text", position: { x: 32, y: 62.5 }, width: 40, height: 3.5, content: "{{PaymentMode}}", fontSize: 7.2, alignment: "right", fontColor: "#000000" },
          { name: "valWords", type: "text", position: { x: 8, y: 66.5 }, width: 64, height: 6, content: "Sum of Rupees: {{AmountInWords}}", fontSize: 6.2, fontColor: "#475569" },

          { name: "amountBannerBg", type: "rectangle", position: { x: 6, y: 75 }, width: 68, height: 13, color: "#0f2d59", borderWidth: 0 },
          { name: "lblTotalAmount", type: "text", position: { x: 6, y: 76.5 }, width: 68, height: 3.5, content: "TOTAL AMOUNT RECEIVED", fontSize: 6.2, alignment: "center", fontColor: "#fbbf24" },
          { name: "valTotalAmount", type: "text", position: { x: 6, y: 80.5 }, width: 68, height: 6.5, content: "₹ {{TotalAmount}}", fontSize: 12.5, alignment: "center", fontColor: "#ffffff" },
          { name: "disclaimerText", type: "text", position: { x: 6, y: 89 }, width: 68, height: 3.2, content: "* All Cheque / DD are subject to realization", fontSize: 5.2, alignment: "center", fontColor: "#64748b" },

          { name: "bullionBox", type: "rectangle", position: { x: 6, y: 93.5 }, width: 68, height: 18, borderWidth: 0.4, borderColor: "#d97706", color: "#fffbeb" },
          { name: "bullionHeader", type: "text", position: { x: 6, y: 94.8 }, width: 68, height: 3.5, content: "BULLION SAVINGS / WEIGHT LEDGER STATEMENT", fontSize: 6.2, alignment: "center", fontColor: "#b45309" },
          { name: "lblRate", type: "text", position: { x: 8, y: 99 }, width: 26, height: 3.5, content: "Rate / Gm:", fontSize: 6.8, fontColor: "#78350f" },
          { name: "valRate", type: "text", position: { x: 34, y: 99 }, width: 38, height: 3.5, content: "₹ {{Rate}}", fontSize: 7.2, alignment: "right", fontColor: "#78350f" },
          { name: "lblWeight", type: "text", position: { x: 8, y: 103 }, width: 26, height: 3.5, content: "Credit Weight:", fontSize: 6.8, fontColor: "#78350f" },
          { name: "valWeight", type: "text", position: { x: 34, y: 103 }, width: 38, height: 3.5, content: "{{Weight}}", fontSize: 7.2, alignment: "right", fontColor: "#78350f" },
          { name: "lblTotalWeight", type: "text", position: { x: 8, y: 107 }, width: 32, height: 3.5, content: "Accumulated Total Weight:", fontSize: 6.8, fontColor: "#78350f" },
          { name: "valTotalWeight", type: "text", position: { x: 40, y: 107 }, width: 32, height: 3.5, content: "{{TotalWeight}}", fontSize: 7.2, alignment: "right", fontColor: "#78350f" },

          { name: "qrCode", type: "qrcode", position: { x: 26, y: 114.5 }, width: 28, height: 28, content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR" },
          { name: "qrCaption", type: "text", position: { x: 6, y: 143.5 }, width: 68, height: 3.5, content: "Scan with GPay / PhonePe / Paytm to Pay", fontSize: 6.2, alignment: "center", fontColor: "#475569" },
          { name: "thankYouMsg", type: "text", position: { x: 6, y: 147.5 }, width: 68, height: 3.5, content: "Thank you for choosing us!", fontSize: 6.8, alignment: "center", fontColor: "#0f2d59" },
          { name: "signLine", type: "line", position: { x: 20, y: 161 }, width: 40, height: 0.3, color: "#94a3b8" },
          { name: "lblReceiverSignature", type: "text", position: { x: 6, y: 162.5 }, width: 68, height: 4.5, content: "Authorized Signatory / Cashier", fontSize: 7.2, alignment: "center", fontColor: "#000000" }
        ]
      ]
    }
  },

  // =======================================================================================
  // 23. A5 MODERN EXECUTIVE RECEIPT VOUCHER (SPLIT HEADER & PASSBOOK CARD)
  // =======================================================================================
  a5ReceiptModern: {
    id: "a5ReceiptModern",
    name: "A5 Modern Executive Receipt Voucher",
    paperSpec: "A5 Sheet / Passbook (148 × 210 mm)",
    voucherType: "RECEIPT",
    width: 148,
    height: 210,
    template: {
      basePdf: {
        width: 148,
        height: 210,
        padding: [6, 6, 6, 6]
      },
      schemas: [
        [
          { name: "outerBorder", type: "rectangle", position: { x: 6, y: 6 }, width: 136, height: 198, borderWidth: 0.6, borderColor: "#0f2d59" },
          { name: "innerBorder", type: "rectangle", position: { x: 7.5, y: 7.5 }, width: 133, height: 195, borderWidth: 0.2, borderColor: "#cbd5e1" },

          // Split Header - Left: Showroom Brand & Logo
          { name: "showroomLogo", type: "image", position: { x: 10, y: 10 }, width: 34, height: 14, content: DEFAULT_PRESET_LOGO },
          { name: "companyName", type: "text", position: { x: 10, y: 25 }, width: 74, height: 6, content: "{{CompanyName}}", fontSize: 13, alignment: "left", fontColor: "#0f2d59" },
          { name: "showroomTagline", type: "text", position: { x: 10, y: 31.5 }, width: 74, height: 3.5, content: "CERTIFIED JEWELLERS & BULLION MERCHANTS", fontSize: 6.5, alignment: "left", fontColor: "#b45309" },
          { name: "showroomWebsite", type: "text", position: { x: 10, y: 35.5 }, width: 74, height: 3.5, content: "Web: {{ShowroomWebsite}}", fontSize: 6.5, alignment: "left", fontColor: "#475569" },

          // Split Header - Right: Executive Voucher Stamp Card
          { name: "metaCardBg", type: "rectangle", position: { x: 88, y: 10 }, width: 50, height: 29, borderWidth: 0.6, borderColor: "#0f2d59" },
          { name: "metaHeaderBg", type: "rectangle", position: { x: 88, y: 10 }, width: 50, height: 7, color: "#0f2d59", borderWidth: 0 },
          { name: "metaHeaderTitle", type: "text", position: { x: 88, y: 11.5 }, width: 50, height: 4.5, content: "★ PAYMENT RECEIPT ★", fontSize: 8, alignment: "center", fontColor: "#ffffff" },
          { name: "lblNo", type: "text", position: { x: 91, y: 19 }, width: 18, height: 4, content: "Receipt #:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valNo", type: "text", position: { x: 110, y: 19 }, width: 25, height: 4, content: "#{{VoucherNo}}", fontSize: 8, alignment: "right", fontColor: "#0f2d59" },
          { name: "lblDate", type: "text", position: { x: 91, y: 24 }, width: 18, height: 4, content: "Date:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valDate", type: "text", position: { x: 110, y: 24 }, width: 25, height: 4, content: "{{Date}}", fontSize: 8, alignment: "right", fontColor: "#000000" },
          { name: "lblAudit", type: "text", position: { x: 90, y: 32 }, width: 46, height: 3.5, content: "ORIGINAL CUSTOMER COPY", fontSize: 5.5, alignment: "center", fontColor: "#0f2d59" },

          { name: "dividerLine", type: "line", position: { x: 10, y: 41 }, width: 128, height: 0.4, color: "#cbd5e1" },

          // Customer Transaction Card
          { name: "custBox", type: "rectangle", position: { x: 10, y: 44 }, width: 128, height: 34, borderWidth: 0.5, borderColor: "#cbd5e1" },
          { name: "lblAccountNo", type: "text", position: { x: 14, y: 47 }, width: 28, height: 4, content: "Account Code:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valAccountNo", type: "text", position: { x: 42, y: 47 }, width: 32, height: 4, content: "{{AccountNo}}", fontSize: 8, fontColor: "#0f2d59" },
          { name: "lblPaymentMode", type: "text", position: { x: 78, y: 47 }, width: 26, height: 4, content: "Payment Mode:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valPaymentMode", type: "text", position: { x: 104, y: 47 }, width: 30, height: 4, content: "{{PaymentMode}}", fontSize: 8, alignment: "right", fontColor: "#000000" },
          { name: "lineCust1", type: "line", position: { x: 14, y: 52.5 }, width: 120, height: 0.2, color: "#e2e8f0" },
          { name: "lblCustomer", type: "text", position: { x: 14, y: 54 }, width: 42, height: 4, content: "Received With Thanks From:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valCustomer", type: "text", position: { x: 57, y: 54 }, width: 77, height: 5, content: "{{CustomerName}}", fontSize: 9.5, fontColor: "#000000" },
          { name: "lineCust2", type: "line", position: { x: 14, y: 60.5 }, width: 120, height: 0.2, color: "#e2e8f0" },
          { name: "lblWords", type: "text", position: { x: 14, y: 62 }, width: 32, height: 4, content: "The Sum of Rupees:", fontSize: 7.5, fontColor: "#64748b" },
          { name: "valWords", type: "text", position: { x: 47, y: 62 }, width: 87, height: 13, content: "{{AmountInWords}}", fontSize: 8, fontColor: "#334155" },

          // Hero Total Amount Banner
          { name: "amountBannerBg", type: "rectangle", position: { x: 10, y: 81 }, width: 128, height: 16, color: "#0f2d59", borderWidth: 0 },
          { name: "lblTotalAmount", type: "text", position: { x: 14, y: 83.5 }, width: 60, height: 4.5, content: "TOTAL AMOUNT RECEIVED", fontSize: 8, alignment: "left", fontColor: "#fbbf24" },
          { name: "disclaimerText", type: "text", position: { x: 14, y: 90 }, width: 60, height: 3.5, content: "* All Cheque / DD subject to realization", fontSize: 5.5, alignment: "left", fontColor: "#93c5fd" },
          { name: "valTotalAmount", type: "text", position: { x: 74, y: 84 }, width: 60, height: 10, content: "₹ {{TotalAmount}}", fontSize: 16, alignment: "right", fontColor: "#ffffff" },

          // Bullion Savings Scheme & Weight Ledger Card
          { name: "bullionBox", type: "rectangle", position: { x: 10, y: 100 }, width: 128, height: 26, borderWidth: 0.5, borderColor: "#d97706", color: "#fffbeb" },
          { name: "bullionHeader", type: "text", position: { x: 14, y: 102 }, width: 120, height: 4, content: "GOLD SCHEME & BULLION WEIGHT LEDGER STATEMENT", fontSize: 7.5, alignment: "center", fontColor: "#b45309" },
          { name: "lblRate", type: "text", position: { x: 14, y: 108 }, width: 38, height: 4, content: "Current Gold Rate / Gm", fontSize: 6.5, alignment: "center", fontColor: "#78350f" },
          { name: "valRate", type: "text", position: { x: 14, y: 113.5 }, width: 38, height: 6, content: "₹ {{Rate}}", fontSize: 10, alignment: "center", fontColor: "#b45309" },
          { name: "lineBullion1", type: "line", position: { x: 53, y: 107 }, width: 0.3, height: 16, color: "#fde68a" },
          { name: "lblWeight", type: "text", position: { x: 55, y: 108 }, width: 38, height: 4, content: "Credited Weight", fontSize: 6.5, alignment: "center", fontColor: "#78350f" },
          { name: "valWeight", type: "text", position: { x: 55, y: 113.5 }, width: 38, height: 6, content: "{{Weight}}", fontSize: 10, alignment: "center", fontColor: "#0f2d59" },
          { name: "lineBullion2", type: "line", position: { x: 94, y: 107 }, width: 0.3, height: 16, color: "#fde68a" },
          { name: "lblTotalWeight", type: "text", position: { x: 96, y: 108 }, width: 38, height: 4, content: "Total Passbook Balance", fontSize: 6.5, alignment: "center", fontColor: "#78350f" },
          { name: "valTotalWeight", type: "text", position: { x: 96, y: 113.5 }, width: 38, height: 6, content: "{{TotalWeight}}", fontSize: 10, alignment: "center", fontColor: "#047857" },

          // Dual Column Footer: Scannable UPI QR & Authorized Cashier Signature
          { name: "qrBox", type: "rectangle", position: { x: 10, y: 130 }, width: 62, height: 68, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "qrHeader", type: "text", position: { x: 12, y: 132 }, width: 58, height: 4, content: "INSTANT UPI PAYMENT", fontSize: 7, alignment: "center", fontColor: "#0f2d59" },
          { name: "qrCode", type: "qrcode", position: { x: 23, y: 137 }, width: 36, height: 36, content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR" },
          { name: "qrCaption", type: "text", position: { x: 12, y: 175 }, width: 58, height: 3.5, content: "Scan with GPay / PhonePe / Paytm", fontSize: 6.5, alignment: "center", fontColor: "#475569" },
          { name: "qrSub", type: "text", position: { x: 12, y: 179 }, width: 58, height: 3, content: "Fast & Secure Digital Payments", fontSize: 5.5, alignment: "center", fontColor: "#94a3b8" },

          { name: "signBox", type: "rectangle", position: { x: 76, y: 130 }, width: 62, height: 68, borderWidth: 0.4, borderColor: "#cbd5e1" },
          { name: "thankYouTitle", type: "text", position: { x: 79, y: 134 }, width: 56, height: 5, content: "Thank You For Your Business!", fontSize: 8.5, alignment: "center", fontColor: "#0f2d59" },
          { name: "thankYouMsg", type: "text", position: { x: 79, y: 140 }, width: 56, height: 9, content: "Computer generated receipt. Please retain this voucher for all future scheme redemptions and gold claims.", fontSize: 6, alignment: "center", fontColor: "#64748b" },
          { name: "signLine", type: "line", position: { x: 86, y: 180 }, width: 42, height: 0.4, color: "#94a3b8" },
          { name: "lblReceiverSignature", type: "text", position: { x: 79, y: 182 }, width: 56, height: 5, content: "Authorized Signatory / Cashier", fontSize: 8, alignment: "center", fontColor: "#000000" }
        ]
      ]
    }
  },

  // =======================================================================================
  // 24. A4 MODERN EXECUTIVE RECEIPT VOUCHER (SPLIT HEADER & PASSBOOK CARD)
  // =======================================================================================
  a4ReceiptModern: {
    id: "a4ReceiptModern",
    name: "A4 Modern Executive Receipt Voucher",
    paperSpec: "Standard A4 Sheet (210 × 297 mm)",
    voucherType: "RECEIPT",
    width: 210,
    height: 297,
    template: {
      basePdf: {
        width: 210,
        height: 297,
        padding: [10, 10, 10, 10]
      },
      schemas: [
        [
          { name: "outerBorder", type: "rectangle", position: { x: 10, y: 10 }, width: 190, height: 277, borderWidth: 0.8, borderColor: "#0f2d59" },
          { name: "innerBorder", type: "rectangle", position: { x: 12, y: 12 }, width: 186, height: 273, borderWidth: 0.25, borderColor: "#cbd5e1" },

          // Split Header - Left: Showroom Brand & Logo
          { name: "showroomLogo", type: "image", position: { x: 15, y: 15 }, width: 48, height: 20, content: DEFAULT_PRESET_LOGO },
          { name: "companyName", type: "text", position: { x: 15, y: 38 }, width: 105, height: 8, content: "{{CompanyName}}", fontSize: 18, alignment: "left", fontColor: "#0f2d59" },
          { name: "showroomTagline", type: "text", position: { x: 15, y: 47 }, width: 105, height: 4.5, content: "CERTIFIED 916 HALLMARKED GOLD • DIAMOND • BULLION MERCHANTS", fontSize: 8.5, alignment: "left", fontColor: "#b45309" },
          { name: "showroomWebsite", type: "text", position: { x: 15, y: 52.5 }, width: 105, height: 4.5, content: "Web: {{ShowroomWebsite}}", fontSize: 8.5, alignment: "left", fontColor: "#475569" },

          // Split Header - Right: Executive Voucher Stamp Card
          { name: "metaCardBg", type: "rectangle", position: { x: 125, y: 15 }, width: 70, height: 42, borderWidth: 0.8, borderColor: "#0f2d59" },
          { name: "metaHeaderBg", type: "rectangle", position: { x: 125, y: 15 }, width: 70, height: 9.5, color: "#0f2d59", borderWidth: 0 },
          { name: "metaHeaderTitle", type: "text", position: { x: 125, y: 17 }, width: 70, height: 6, content: "★ OFFICIAL PAYMENT RECEIPT ★", fontSize: 9.5, alignment: "center", fontColor: "#ffffff" },
          { name: "lblNo", type: "text", position: { x: 128, y: 27 }, width: 24, height: 5, content: "Receipt No:", fontSize: 9, fontColor: "#64748b" },
          { name: "valNo", type: "text", position: { x: 152, y: 27 }, width: 40, height: 5, content: "#{{VoucherNo}}", fontSize: 10, alignment: "right", fontColor: "#0f2d59" },
          { name: "lblDate", type: "text", position: { x: 128, y: 35 }, width: 24, height: 5, content: "Date:", fontSize: 9, fontColor: "#64748b" },
          { name: "valDate", type: "text", position: { x: 152, y: 35 }, width: 40, height: 5, content: "{{Date}}", fontSize: 10, alignment: "right", fontColor: "#000000" },
          { name: "lblAudit", type: "text", position: { x: 128, y: 46 }, width: 64, height: 4.5, content: "ORIGINAL AUDIT VOUCHER", fontSize: 7, alignment: "center", fontColor: "#0f2d59" },

          { name: "dividerLine", type: "line", position: { x: 15, y: 61 }, width: 180, height: 0.5, color: "#cbd5e1" },

          // Customer Transaction Card
          { name: "custBox", type: "rectangle", position: { x: 15, y: 65 }, width: 180, height: 46, borderWidth: 0.6, borderColor: "#cbd5e1" },
          { name: "lblAccountNo", type: "text", position: { x: 20, y: 69 }, width: 35, height: 5, content: "Account Code:", fontSize: 10, fontColor: "#64748b" },
          { name: "valAccountNo", type: "text", position: { x: 55, y: 69 }, width: 45, height: 5, content: "{{AccountNo}}", fontSize: 11, fontColor: "#0f2d59" },
          { name: "lblPaymentMode", type: "text", position: { x: 105, y: 69 }, width: 35, height: 5, content: "Payment Mode:", fontSize: 10, fontColor: "#64748b" },
          { name: "valPaymentMode", type: "text", position: { x: 140, y: 69 }, width: 50, height: 5, content: "{{PaymentMode}}", fontSize: 11, alignment: "right", fontColor: "#000000" },
          { name: "lineCust1", type: "line", position: { x: 20, y: 76 }, width: 170, height: 0.3, color: "#e2e8f0" },
          { name: "lblCustomer", type: "text", position: { x: 20, y: 78 }, width: 55, height: 5, content: "Received With Thanks From:", fontSize: 10, fontColor: "#64748b" },
          { name: "valCustomer", type: "text", position: { x: 75, y: 78 }, width: 115, height: 6, content: "{{CustomerName}}", fontSize: 12.5, fontColor: "#000000" },
          { name: "lineCust2", type: "line", position: { x: 20, y: 86.5 }, width: 170, height: 0.3, color: "#e2e8f0" },
          { name: "lblWords", type: "text", position: { x: 20, y: 89 }, width: 45, height: 5, content: "The Sum of Rupees:", fontSize: 10, fontColor: "#64748b" },
          { name: "valWords", type: "text", position: { x: 65, y: 89 }, width: 125, height: 18, content: "{{AmountInWords}}", fontSize: 10.5, fontColor: "#334155" },

          // Hero Total Amount Banner
          { name: "amountBannerBg", type: "rectangle", position: { x: 15, y: 115 }, width: 180, height: 22, color: "#0f2d59", borderWidth: 0 },
          { name: "lblTotalAmount", type: "text", position: { x: 22, y: 118 }, width: 80, height: 6, content: "TOTAL AMOUNT RECEIVED", fontSize: 10, alignment: "left", fontColor: "#fbbf24" },
          { name: "disclaimerText", type: "text", position: { x: 22, y: 127 }, width: 80, height: 4.5, content: "* All Cheque / DD subject to realization", fontSize: 7, alignment: "left", fontColor: "#93c5fd" },
          { name: "valTotalAmount", type: "text", position: { x: 105, y: 118.5 }, width: 85, height: 14, content: "₹ {{TotalAmount}}", fontSize: 22, alignment: "right", fontColor: "#ffffff" },

          // Bullion Savings Scheme & Weight Ledger Card
          { name: "bullionBox", type: "rectangle", position: { x: 15, y: 141 }, width: 180, height: 35, borderWidth: 0.6, borderColor: "#d97706", color: "#fffbeb" },
          { name: "bullionHeader", type: "text", position: { x: 20, y: 144 }, width: 170, height: 5, content: "JEWELLERY SAVINGS SCHEME & BULLION WEIGHT LEDGER", fontSize: 9.5, alignment: "center", fontColor: "#b45309" },
          { name: "lblRate", type: "text", position: { x: 20, y: 152 }, width: 50, height: 5, content: "Current Gold Rate / Gm", fontSize: 8.5, alignment: "center", fontColor: "#78350f" },
          { name: "valRate", type: "text", position: { x: 20, y: 159 }, width: 50, height: 8, content: "₹ {{Rate}}", fontSize: 14, alignment: "center", fontColor: "#b45309" },
          { name: "lineBullion1", type: "line", position: { x: 74, y: 150 }, width: 0.4, height: 21, color: "#fde68a" },
          { name: "lblWeight", type: "text", position: { x: 80, y: 152 }, width: 50, height: 5, content: "Credited Weight", fontSize: 8.5, alignment: "center", fontColor: "#78350f" },
          { name: "valWeight", type: "text", position: { x: 80, y: 159 }, width: 50, height: 8, content: "{{Weight}}", fontSize: 14, alignment: "center", fontColor: "#0f2d59" },
          { name: "lineBullion2", type: "line", position: { x: 134, y: 150 }, width: 0.4, height: 21, color: "#fde68a" },
          { name: "lblTotalWeight", type: "text", position: { x: 140, y: 152 }, width: 50, height: 5, content: "Total Passbook Balance", fontSize: 8.5, alignment: "center", fontColor: "#78350f" },
          { name: "valTotalWeight", type: "text", position: { x: 140, y: 159 }, width: 50, height: 8, content: "{{TotalWeight}}", fontSize: 14, alignment: "center", fontColor: "#047857" },

          // Dual Column Footer: Scannable UPI QR & Authorized Cashier Signature
          { name: "qrBox", type: "rectangle", position: { x: 15, y: 180 }, width: 86, height: 95, borderWidth: 0.6, borderColor: "#cbd5e1" },
          { name: "qrHeader", type: "text", position: { x: 18, y: 184 }, width: 80, height: 5.5, content: "INSTANT UPI PAYMENT", fontSize: 9.5, alignment: "center", fontColor: "#0f2d59" },
          { name: "qrCode", type: "qrcode", position: { x: 33, y: 193 }, width: 50, height: 50, content: "upi://pay?pa=shergold@upi&pn={{CompanyName}}&cu=INR" },
          { name: "qrCaption", type: "text", position: { x: 18, y: 247 }, width: 80, height: 5, content: "Scan with GPay / PhonePe / Paytm to Pay", fontSize: 8.5, alignment: "center", fontColor: "#475569" },
          { name: "qrSub", type: "text", position: { x: 18, y: 254 }, width: 80, height: 4, content: "Supported across all NPCI Unified Payments Apps", fontSize: 7, alignment: "center", fontColor: "#94a3b8" },

          { name: "signBox", type: "rectangle", position: { x: 109, y: 180 }, width: 86, height: 95, borderWidth: 0.6, borderColor: "#cbd5e1" },
          { name: "thankYouTitle", type: "text", position: { x: 114, y: 186 }, width: 76, height: 6.5, content: "Thank You For Your Business!", fontSize: 11.5, alignment: "center", fontColor: "#0f2d59" },
          { name: "thankYouMsg", type: "text", position: { x: 114, y: 195 }, width: 76, height: 16, content: "This is a computer generated payment receipt. Please retain this original voucher for gold delivery claims, passbook balancing, and scheme redemptions.", fontSize: 8, alignment: "center", fontColor: "#64748b" },
          { name: "signLine", type: "line", position: { x: 122, y: 248 }, width: 60, height: 0.5, color: "#94a3b8" },
          { name: "lblReceiverSignature", type: "text", position: { x: 114, y: 251 }, width: 76, height: 6, content: "Authorized Signatory / Cashier", fontSize: 10, alignment: "center", fontColor: "#000000" }
        ]
      ]
    }
  }
};

// =========================================================================================
// BACKWARD COMPATIBILITY ALIASES
SAMPLE_TEMPLATES.a4Invoice = SAMPLE_TEMPLATES.a4Sales;
SAMPLE_TEMPLATES.thermal80mm = SAMPLE_TEMPLATES.thermal80mmSale;
SAMPLE_TEMPLATES.thermal58mm = SAMPLE_TEMPLATES.thermal58mmSale;
SAMPLE_TEMPLATES.thermal57mm = SAMPLE_TEMPLATES.thermal58mmSale;
SAMPLE_TEMPLATES.thermal57mmSale = SAMPLE_TEMPLATES.thermal58mmSale;
SAMPLE_TEMPLATES.thermal57mmPurchase = SAMPLE_TEMPLATES.thermal58mmPurchase;
SAMPLE_TEMPLATES.thermal58mmReceiptBranded = SAMPLE_TEMPLATES.thermal58mmReceiptSmart;
SAMPLE_TEMPLATES.thermal80mmReceiptBranded = SAMPLE_TEMPLATES.thermal80mmReceiptSmart;
SAMPLE_TEMPLATES.a5ReceiptBranded = SAMPLE_TEMPLATES.a5ReceiptSmart;
SAMPLE_TEMPLATES.a4ReceiptBranded = SAMPLE_TEMPLATES.a4ReceiptSmart;



